import {
  useRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useEffectEvent,
  forwardRef,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { ActionType, type DomPosition } from 'klinecharts'
import type { EdgeChartProps, EdgeChartApi, ViewportSyncData } from '../types/chart'
import {
  PERIODS,
  alignTimestamp,
  isDailyPeriod,
  type Period,
  type PeriodKey,
} from '../utils/resolution'
import { ChartManager, type AnnotationTextEditRequest } from '../core/ChartManager'
import { Toolbar } from './Toolbar'
import { DrawingBar, type HideMode } from './DrawingBar'
import type { DrawingToolsBundle } from './DrawingToolsMenu'
import { useToolWheelTarget } from './toolWheelTargets'
import type { ActiveIndicator } from './IndicatorLegend'
import { IndicatorLegendLayer } from './IndicatorLegendLayer'
import {
  externalHasInputs,
  externalHasStyles,
  externalLegendRow,
  externalOpensNativeDrawer,
} from '../utils/external-legend-row'
import { PositionSizeReadout } from './PositionSizeReadout'
import { PlanWeightMenu } from './PlanWeightMenu'
import { AnnotationTextInput } from './AnnotationTextInput'
import { rightRailRight, rightRailTop } from './overlayRailTop'
import { POSITION_SIZE_NAME } from '../utils/positionSizeIndicator'
import { cascadeWeights, type PlanWeightEditRequest } from '../utils/positionPlanOverlay'
import { IndicatorSettingsDrawer } from './IndicatorSettingsDrawer'
import { OverlaySettingsDrawer, type OverlayEditTarget } from './OverlaySettingsDrawer'
import { OverlayQuickBar } from './OverlayQuickBar'
import { LoadingOlderIndicator } from './LoadingOlderIndicator'
import { SettingsModal } from './SettingsModal'
import { DEFAULT_SETTINGS, styleGroupOf, type ChartSettings } from './chartSettings'
import {
  HEIKIN_ASHI_INDICATOR_NAME,
  heikinAshiStyleOf,
  registerHeikinAshiIndicator,
} from '../utils/heikinAshiIndicator'
import { DEFAULT_INTERACTION_SETTINGS } from '../types/interaction'
import { applyTheme } from '../themes/base'
import { chartColorDefaults, effectiveColor } from '../utils/themedColors'
import { PriceScaleContextMenu } from './PriceScaleContextMenu'
import { ChartBodyContextMenu } from './ChartBodyContextMenu'
import { ReplayControls } from './ReplayControls'
import { useReplayHotkeys } from '../hooks/useReplayHotkeys'
import { MAIN_INDICATORS, SUB_INDICATORS, formatIndicatorLabel } from '../utils/indicators'
import {
  getIndicatorDef,
  buildIndicatorStyles,
  type IndicatorStyleOverrides,
} from '@edge/charts-indicators'
import { layerIndicatorUserDefault, setIndicatorUserDefault } from '../utils/indicatorUserDefaults'
import {
  selfStyledSwatch,
  resolveOverlayDefault,
  setOverlayUserDefault,
} from '../utils/overlayUserDefaults'
import { candleStylesForChartType } from '../utils/chartTypeStyles'
import { CHART_COLORS, CHART_FONT_FAMILY } from '../utils/chartColors'
import {
  normalizeIndicatorEntry,
  type IndicatorEntry,
  type OverlayStyleOverride,
} from '../types/persistence'
import {
  BUILT_IN_TIMEFRAME_VISIBILITY,
  evaluateTimeframeVisibility,
  type TimeframeVisibilityRule,
} from '../utils/timeframeVisibility'
import {
  isDuplicableIndicator,
  isMtfEligibleIndicator,
  allocateInstanceName,
  ensureInstanceRegistered,
} from '../utils/maInstances'
import { useCrosshairSync } from '../hooks/useCrosshairSync'
import { useViewportSync } from '../hooks/useViewportSync'
import { useOverlaySync, useDrawingRestore } from '../hooks/useOverlaySync'
import { useChartResize } from '../hooks/useChartResize'
import { useScrollTracking } from '../hooks/useScrollTracking'
import { useBarCountdown, barCountdownEnabled } from '../hooks/useBarCountdown'
import { useMarketSession } from '../hooks/useMarketSession'
import { useEarningsMarkers } from '../hooks/useEarningsMarkers'
import { useSplitMarkers } from '../hooks/useSplitMarkers'
import { useDilutionLines } from '../hooks/useDilutionLines'
import { useChartStyles } from '../hooks/useChartStyles'
import { useIntervalQuickSwitch } from '../hooks/useIntervalQuickSwitch'
import { ChangeIntervalOverlay } from './ChangeIntervalOverlay'
import { registerSessionHighlightIndicator } from '../utils/sessionHighlight'
import { registerRangeHighlightIndicator } from '../utils/rangeHighlight'
import { registerSessionBreaksIndicator } from '../utils/sessionBreaks'
import { registerPreviousCloseIndicator } from '../utils/previousClose'
import { registerPrevDayHighLowIndicators } from '../utils/previousDayHighLow'
import { registerDayOpenIndicator } from '../utils/openLine'
import { registerVolumeIndicator, VOL_OVERLAY_NAME } from '../utils/volumeIndicator'
import { registerMacdIndicator } from '../utils/macdIndicator'
import { registerTpoIndicator, TPO_INDICATOR_NAME } from '../utils/tpoIndicator'
import { registerHtfIndicator, HTF_INDICATOR_NAME } from '../utils/htfIndicator'
import { registerAutoFibOverlay, registerFibonacciOverlay } from '../utils/fibonacciOverlay'
import { registerRiskRewardOverlay } from '../utils/riskRewardOverlay'
import { registerMeasureOverlay } from '../utils/measureOverlay'
import { registerReplayLoopOverlay } from '../utils/replayLoopOverlay'
import { registerShapeOverlays } from '../utils/shapeOverlays'
import { CANDLE_PANE_ID } from '../types/chart'
import { registerArrowOverlays } from '../utils/arrowOverlays'
import { registerAnnotationOverlay } from '../utils/annotationOverlay'
import { registerTagOverlay } from '../utils/tagOverlay'
import { registerRangeOverlays } from '../utils/rangeOverlays'
import { registerHorizontalRayOverlay } from '../utils/horizontalRayOverlay'
import { registerHorizontalLineOverlay } from '../utils/horizontalLineOverlay'
import {
  registerVerticalLineOverlay,
  registerVerticalLineSpanOverlay,
} from '../utils/verticalLineOverlay'
import { OhlcHeader } from './OhlcHeader'
import { CrosshairTimeLabel } from './CrosshairTimeLabel'
import { CrosshairPriceAction } from './CrosshairPriceAction'
import type { PriceAlertLine } from '../utils/priceAlertLine'
import { EarningsTooltip } from './EarningsTooltip'
import { SplitTooltip } from './SplitTooltip'
import { registerEarningsMarkersIndicator } from '../utils/earningsMarkersIndicator'
import { registerSplitMarkersIndicator } from '../utils/splitMarkersIndicator'
import { registerArticleMarkerOverlay } from '../utils/articleMarkerOverlay'
import { registerEarningsMarkerOverlay } from '../utils/earningsMarkerOverlay'
import { registerTradeFillMarkerOverlay } from '../utils/tradeFillMarkerOverlay'
import { registerDilutionPriceLineOverlay } from '../utils/dilutionPriceLine'
import { registerVwapIndicator } from '../utils/vwapIndicator'
import { registerPivotPointsIndicator } from '../utils/pivotPointsIndicator'
import { registerPositionSizeIndicator } from '../utils/positionSizeIndicator'
import { registerPositionPlanOverlay } from '../utils/positionPlanOverlay'
import { registerVolumeProfileIndicator } from '../utils/volumeProfileIndicator'
import { registerTaLibIndicators } from '../utils/talibIndicators'
import { registerMaBandIndicators } from '../utils/maBandIndicators'
import { registerPatternOverlay } from '../utils/patternOverlay'
import { registerIndicatorFillEnhancements } from '../utils/indicatorFills'

// Register custom indicators/overlays once at module load
registerSessionHighlightIndicator()
registerRangeHighlightIndicator()
registerSessionBreaksIndicator()
registerPreviousCloseIndicator()
registerPrevDayHighLowIndicators()
registerDayOpenIndicator()
registerEarningsMarkersIndicator()
registerSplitMarkersIndicator()
registerVolumeIndicator()
registerMacdIndicator()
registerIndicatorFillEnhancements()
registerFibonacciOverlay()
registerAutoFibOverlay()
registerRiskRewardOverlay()
registerPositionPlanOverlay()
registerMeasureOverlay()
registerReplayLoopOverlay()
registerShapeOverlays()
registerArrowOverlays()
registerAnnotationOverlay()
registerTagOverlay()
registerRangeOverlays()
registerHorizontalRayOverlay()
registerHorizontalLineOverlay()
registerVerticalLineOverlay()
registerVerticalLineSpanOverlay()
registerArticleMarkerOverlay()
registerEarningsMarkerOverlay()
registerTradeFillMarkerOverlay()
registerDilutionPriceLineOverlay()
registerVwapIndicator()
registerPivotPointsIndicator()
registerPositionSizeIndicator()
registerVolumeProfileIndicator()
registerTpoIndicator()
registerHeikinAshiIndicator()
registerHtfIndicator()
registerTaLibIndicators()
registerMaBandIndicators()
registerPatternOverlay()

// Default line colors handed to the 2nd, 3rd, … instance of a duplicated
// MA-family overlay so stacked EMAs/MAs are visually distinct out of the box.
const DUP_PALETTE = ['#FF9600', '#935EBD', '#2196F3', '#00BCD4', '#26A69A']

// Sub-pane height persistence. OnPaneDrag fires continuously through a drag, so
// capture is debounced to the settle point. Ratios within the epsilon are
// treated as unchanged — this is the feedback-loop guard: after a drag persists
// and the entry flows back as a prop, the dragged chart already sits at the
// captured ratio, so the diff effect's restore is a no-op (only siblings, whose
// panes still differ, adjust once).
const PANE_HEIGHT_CAPTURE_DEBOUNCE_MS = 400
const PANE_HEIGHT_RATIO_EPSILON = 0.01

// Stable, order-sensitive fingerprint of the desired indicator props. The
// reconciler compares this against the last-applied fingerprint to decide
// whether anything changed — and to make the first reconcile after init a
// no-op (the init effect seeds the same set). Shape mirrors the chart-panel
// cell key's `JSON.stringify({ m, s })` so the two stay aligned.
function indicatorSignature(main: IndicatorEntry[], sub: IndicatorEntry[]): string {
  const norm = (arr: IndicatorEntry[]) =>
    arr.map(normalizeIndicatorEntry).map((e) => ({
      name: e.name,
      calcParams: e.calcParams ?? null,
      styles: e.styles ?? null,
      visible: e.visible ?? true,
      placement: e.placement ?? null,
      timeframeVisibility: e.timeframeVisibility ?? null,
      sourceTimeframe: e.sourceTimeframe ?? null,
      // Part of the fingerprint so a ratio-only prop change wakes the diff
      // effect (it then restores sub-pane heights without re-creating anything).
      paneHeightRatio: e.paneHeightRatio ?? null,
    }))
  return JSON.stringify({ m: norm(main), s: norm(sub) })
}

/**
 * Effective on-chart visibility = the manual eye toggle AND the per-timeframe
 * rule permitting the current period. Both gates must pass for the indicator to
 * draw; the manual `visible` flag is preserved independently so switching
 * timeframes never loses the user's explicit choice.
 *
 * `name` (the indicator's BASE name, not an MA-family instance alias) enables
 * the built-in catalog fallback: an entry with no explicit rule inherits
 * `BUILT_IN_TIMEFRAME_VISIBILITY[name]` (e.g. VWAP hidden on daily+), so
 * PRE-EXISTING persisted entries — created before the built-in existed or
 * inherited into a daily drilldown — hide correctly too. An entry carrying an
 * explicit rule (even an empty `{}` = "all timeframes") always wins.
 */
// themeVersion is the recompute key: a same-mode preset switch can keep the theme's identity.
function resolveSettingsThemeColors(
  theme: EdgeChartProps['theme'],
  settings: ChartSettings,
  _themeVersion: number | undefined,
) {
  return chartColorDefaults(applyTheme(theme), settings)
}

function effectiveIndicatorVisible(
  visible: boolean | undefined,
  rule: TimeframeVisibilityRule | undefined,
  period: Period,
  name?: string,
): boolean {
  const effRule = rule ?? (name ? BUILT_IN_TIMEFRAME_VISIBILITY[name] : undefined)
  return visible !== false && evaluateTimeframeVisibility(effRule, period)
}

/* ------------------------------------------------------------------ */
/*  Color tokens                                                       */
/* ------------------------------------------------------------------ */

const C = {
  bg: CHART_COLORS.canvas,
  text: CHART_COLORS.text,
  textSecond: CHART_COLORS.textMuted,
  border: CHART_COLORS.border,
  primary: CHART_COLORS.primary,
  hover: CHART_COLORS.primaryFade,
}

/** Vertical clearance for the floating toolbar chips (top: 6px + 22px chip). */
const TOOLBAR_OVERLAY_INSET = 34
/** Horizontal clearance for the expanded floating drawing bar (flush left, 36px wide, plus a gap). */
const DRAWING_BAR_OVERLAY_INSET = 46
/** Legend row pitch (20px row + 1px gap) — the stack math's existing rhythm. */
const LEGEND_ROW_H = 21
/** How far an Annotation's label sits above its anchor: 6px gap + 50px stem +
 *  5px arrowhead (annotationOverlay.ts). The text box opens just above it. */
const ANNOTATION_STEM_RISE = 61
/** AnnotationTextInput's box: 160px field + 3px padding + 1px border a side. */
const ANNOTATION_INPUT_W = 168
const ANNOTATION_INPUT_H = 29
const ANNOTATION_INPUT_GAP = 4
const NO_PRICE_ALERT_LINES: PriceAlertLine[] = []

/**
 * Read a klinecharts `OverlayStyle` (as seen on `event.overlay.styles`) back
 * into our compact `OverlayStyleOverride`. Only `line.{color,size,style}` are
 * surfaced; anything else falls through to the overlay's defaults.
 */
function readOverlayLineStyle(styles: unknown): OverlayStyleOverride | undefined {
  const line = (styles as { line?: { color?: unknown; size?: unknown; style?: unknown } } | null)
    ?.line
  if (!line) return undefined
  const out: OverlayStyleOverride = {}
  if (typeof line.color === 'string') out.color = line.color
  if (typeof line.size === 'number') out.size = line.size
  if (line.style === 'solid' || line.style === 'dashed') out.style = line.style
  return Object.keys(out).length > 0 ? out : undefined
}

/* ------------------------------------------------------------------ */
/*  EdgeChart                                                          */
/* ------------------------------------------------------------------ */

export const EdgeChart = forwardRef<EdgeChartApi, EdgeChartProps>(function EdgeChart(props, ref) {
  const {
    symbol,
    period: initialPeriod,
    datafeed,
    theme = 'dark',
    themeVersion,
    timezone,
    // Hidden by default — the floating drawing bar auto-collapses to a pencil
    // tab whenever the cursor leaves the chart, so it starts collapsed too.
    drawingBarVisible: initialDrawingBarVisible = false,
    mainIndicators,
    subIndicators,
    styles,
    className,
    style,
    chartId,
    crosshairSync,
    crosshairTickerSync = true,
    crosshairGlobalSync = false,
    viewportSync,
    overlaySyncGroup,
    initialChartType,
    initialSettings,
    initialMainIndicators,
    initialSubIndicators,
    globalSettings,
    interactionSettings = DEFAULT_INTERACTION_SETTINGS,
    onInteractionSettingsChange = () => {},
    onSettingsChange,
    onChartTypeChange: onChartTypeChangeProp,
    onIndicatorsChange,
    initialHiddenState,
    onHiddenStateChange,
    onToggleVisibilityAll,
    onDrawingEvent,
    onRestoreDiagnostic,
    savedDrawings,
    templates,
    onSaveTemplate,
    onLoadTemplate,
    onDeleteTemplate,
    onUpdateTemplate,
    onLayout,
    layoutActive,
    onDrillDown,
    toolbarControlsVisible = true,
    toolbarTitle,
    toolbarVisible: toolbarVisibleProp = true,
    toolbarTimeframeOnly = false,
    overlayTopInset = 0,
    overlayLeading,
    drawingToolsEnabled = false,
    onDrawingBarVisibilityChange,
    drawingFavorites,
    onToggleDrawingFavorite,
    onDrawingToolsChange,
    initialFocusTimestamp,
    anchorVisibleWindow,
    rangeHighlight,
    historical = false,
    referencePrice,
    extendedSessionPrice,
    watermarkSubtitle,
    showWatermark = true,
    initialBarSpace,
    enablePreload,
    onReady,
    onPeriodChange,
    onVisibleRangeChange,
    onReplayStep,
    favoriteTimeframes,
    onToggleFavoriteTimeframe,
    legendCollapsed,
    onToggleLegendCollapse,
    onIntradayDrill,
    onReplayFromDay,
    onAskAiAboutDay,
    onTagDay,
    onExcludeTicker,
    autoTracesEnabled,
    onToggleAutoTraces,
    onFindSimilarPattern,
    findSimilarScopes,
    settingsProfiles,
    onSaveSettingsProfile,
    onLoadSettingsProfile,
    onDeleteSettingsProfile,
    onApplyIndicatorToAllCharts,
    onMaximize,
    isMaximized: isMaximizedProp,
    externalIndicators,
    onReplayActiveChange,
    earnings,
    dilutionLevels,
    splits,
    priceAlertLines,
    onAddPriceAlert,
    onCancelPriceAlert,
    priceAlertsAvailable,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const managerRef = useRef<ChartManager | null>(null)
  const drawingsRestoredRef = useRef(false)

  const [period, setPeriod] = useState<Period>(initialPeriod)
  // Mirror of `period` for imperative reads inside effects/handlers whose deps
  // must not include period (e.g. the prop reconciler that keys off signature).
  const periodRef = useRef(period)
  periodRef.current = period
  const [chartType, setChartType] = useState(initialChartType ?? 'candle_solid')
  // The currently-armed drawing tool (overlay name), or null. Drives the single
  // active-button highlight in the DrawingBar. Cleared by the manager's
  // deactivation callback when a draw completes or is cancelled, so the
  // highlight drops once the drawing is placed.
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [drawingBarVisible, setDrawingBarVisible] = useState(initialDrawingBarVisible)
  // Cursor presence over the chart. SEPARATE from `drawingBarVisible`, which is
  // the user's persisted INTENT. Leaving the chart tucks the bar away by
  // flipping this; it must never clear the intent, or returning has nothing to
  // restore. (It used to clear the intent — and persist the clear — so an
  // opened bar was forgotten the moment the cursor left.)
  // Starts TRUE: a chart mounting with intent already open should show the bar
  // immediately, not wait for the pointer to move. (No mouseenter fires when a
  // component mounts under a stationary cursor — the same staleness
  // `usePanelHover` documents.) The first genuine mouseleave tucks it away.
  const [drawingBarHovered, setDrawingBarHovered] = useState(true)
  // Follow the controlled prop so external toggles (e.g. the panel's `A` hotkey,
  // which persists drawingBarVisible) actually move the bar — not only the
  // in-bar collapse button. That button also sets this state directly for
  // instant feedback and echoes the same value back through the prop, so there
  // is no update loop.
  useEffect(() => {
    setDrawingBarVisible(initialDrawingBarVisible)
  }, [initialDrawingBarVisible])
  // isMaximized: controlled by host (via isMaximizedProp + onMaximize) or internal.
  const [internalMaximized, setInternalMaximized] = useState(false)
  const isMaximized = onMaximize !== undefined ? (isMaximizedProp ?? false) : internalMaximized
  const [snapMode, setSnapMode] = useState(false)
  const [lockAll, setLockAll] = useState(false)
  const [hiddenState, setHiddenState] = useState({
    drawings: initialHiddenState?.drawings ?? false,
    indicators: initialHiddenState?.indicators ?? false,
  })
  const hiddenStateRef = useRef(hiddenState)
  hiddenStateRef.current = hiddenState
  // What the ENGINE is doing, which is what the legend must show. Mirrors
  // ChartManager's flag via its 'indicators:hidden' event, because a grid's
  // shared eye reaches the manager directly and never touches hiddenState.
  const [indicatorsEyeHidden, setIndicatorsEyeHidden] = useState(
    initialHiddenState?.indicators ?? false,
  )
  const onHiddenStateChangeRef = useRef(onHiddenStateChange)
  onHiddenStateChangeRef.current = onHiddenStateChange
  const [settingsOpen, setSettingsOpen] = useState(false)
  // Viewport point of the click that opened settings — the modal opens
  // adjacent to it instead of pinned to the screen edge.
  // Right-click price-axis menu — a fast path into the price-scale settings.
  // Only one of this / the settings modal is open at a time.
  // `alertPrice` is the "Add alert at $X" row's price (ChartManager.alertPriceAt); null hides the row.
  const [priceScaleMenu, setPriceScaleMenu] = useState<{
    x: number
    y: number
    alertPrice: { price: number; text: string } | null
  } | null>(null)
  // Right-click chart-BODY menu — view / drawing / indicator / type / settings
  // plus the day-scoped replay / ask-AI / drill actions. `dayTs` is the trading
  // day (midnight-UTC aligned) of the bar under the cursor, or null on empty
  // canvas (which hides the day-scoped rows). Sibling to priceScaleMenu.
  //
  // `barTs` is the SAME bar's raw, unaligned timestamp. Tagging needs it because
  // the host renders the date in the chart's configured timezone — feeding it
  // the UTC-midnight `dayTs` instead would shift any evening bar by a day.
  const [bodyMenu, setBodyMenu] = useState<{
    x: number
    y: number
    dayTs: number | null
    barTs: number | null
    /** The "Add alert at $X" row's price (ChartManager.alertPriceAt); null hides the row. */
    alertPrice: { price: number; text: string } | null
  } | null>(null)
  const [activeIndicators, setActiveIndicators] = useState<ActiveIndicator[]>([])
  const activeIndicatorsRef = useRef(activeIndicators)
  activeIndicatorsRef.current = activeIndicators
  // Fingerprint of the last indicator set applied from props. Seeded by the
  // init effect so the first reconcile pass is a no-op; updated each time the
  // reconciler applies a prop-driven change. See `indicatorSignature`.
  const lastIndicatorSignatureRef = useRef<string | null>(null)
  // Held true while we push a persisted sub-pane height into klinecharts, so the
  // OnPaneDrag capture subscription ignores the programmatic change and never
  // re-persists it. (klinecharts only fires OnPaneDrag on real mouse drags —
  // see SeparatorWidget — so this is defensive, not load-bearing.)
  const applyingPaneHeightsRef = useRef(false)
  const [editingIndicatorKey, setEditingIndicatorKey] = useState<string | null>(null)
  // Viewport point of the legend-gear click, so the indicator settings drawer
  // anchors beside the click instead of centering (null → keyboard/center).
  // Drawing (overlay) settings drawer target, opened on double-click via the
  // engine's `overlay:edit` event. Only one of this / the indicator drawer /
  // the settings modal / the price-scale menu is open at a time.
  const [overlayEdit, setOverlayEdit] = useState<OverlayEditTarget | null>(null)
  // Single-click selection → floating quick-edit bar. Distinct from
  // `overlayEdit` (double-click → full drawer); both can be open at once.
  const [quickBar, setQuickBar] = useState<{
    id: string
    name: string
    styles: OverlayStyleOverride | undefined
    locked: boolean
    /** Anchor-first points, kept so the quick bar can edit coordinates. */
    points?: Array<{ timestamp?: number; value?: number }>
    /** Per-instance state (an annotation's label), forwarded by the gear so
     *  the settings drawer opens on the current value. */
    extendData?: unknown
  } | null>(null)
  // Minimize the price-pane indicator legend to a single chevron.
  // Controlled when the host passes `onToggleLegendCollapse` (e.g. the
  // scanner shares one collapse state across every cell); otherwise the chart
  // owns an internal, ephemeral state that resets to expanded on reload.
  const [internalLegendCollapsed, setInternalLegendCollapsed] = useState(false)
  const legendControlled = onToggleLegendCollapse != null
  const mainLegendCollapsed = legendControlled ? !!legendCollapsed : internalLegendCollapsed
  const toggleLegendCollapse = legendControlled
    ? onToggleLegendCollapse
    : () => setInternalLegendCollapsed((v) => !v)
  const [scrollToLatestPos, setScrollToLatestPos] = useState<{
    bottom: number
    right: number
  } | null>(null)
  const [settings, setSettings] = useState<ChartSettings>({
    ...DEFAULT_SETTINGS,
    timezone: timezone ?? DEFAULT_SETTINGS.timezone,
    ...initialSettings,
  })
  // The `-ticker` invert shortcut (and the workspace panel-header) flip
  // `priceScaleInvert` on the persisted settings from OUTSIDE the chart. EdgeChart
  // otherwise only picks up in-chart modal edits + `globalSettings` (absent on a
  // standalone chart), so the live axis never flipped. Mirror JUST this one field
  // reactively — a blanket `initialSettings` merge would clobber other settings
  // the user is editing in-chart. Idempotent (skips when already in sync).
  const externalInvert = initialSettings?.priceScaleInvert
  useEffect(() => {
    if (externalInvert === undefined) return
    setSettings((prev) =>
      prev.priceScaleInvert === externalInvert
        ? prev
        : { ...prev, priceScaleInvert: externalInvert },
    )
  }, [externalInvert])
  const [chartInstance, setChartInstance] = useState<ReturnType<ChartManager['getChart']> | null>(
    null,
  )
  // Bar-replay mode: driven by the manager's `replay:started` / `replay:ended`
  // events. Renders the floating ReplayControls strip and arms the Escape-pauses
  // capture handler. Ephemeral — never persisted (a page reload comes back live).
  const [replayActive, setReplayActive] = useState(false)
  // Loop-range selection armed (loop button pressed, range clicks pending).
  const [replayLoopArming, setReplayLoopArming] = useState(false)
  const onReplayActiveChangeRef = useRef(onReplayActiveChange)
  onReplayActiveChangeRef.current = onReplayActiveChange

  // Host-managed (external) indicators folded into the legend system. The
  // host owns their klinecharts lifecycle; EdgeChart only renders legend rows
  // and routes button actions back through the entry callbacks. They are
  // deliberately NOT in `activeIndicators` state, so the prop reconciler and
  // `onIndicatorsChange` persistence never see them.
  const externalByKeyRef = useRef(new Map<string, NonNullable<typeof externalIndicators>[number]>())
  externalByKeyRef.current = new Map((externalIndicators ?? []).map((e) => [e.key, e]))
  const externalActive = useMemo<ActiveIndicator[]>(
    () => (externalIndicators ?? []).map(externalLegendRow),
    [externalIndicators],
  )
  const legendIndicators = useMemo(() => {
    // While the eye hides price-pane indicators, their rows read as OFF so the
    // legend matches the chart (Chris, 2026-09-25). Display only: the toggle
    // handler reads the real per-row flag from activeIndicatorsRef.
    const own = indicatorsEyeHidden
      ? activeIndicators.map((i) => (i.paneId === CANDLE_PANE_ID ? { ...i, visible: false } : i))
      : activeIndicators
    return externalActive.length ? [...own, ...externalActive] : own
  }, [activeIndicators, externalActive, indicatorsEyeHidden])

  // The handle is built once, so its methods read the host callbacks from here.
  const handleCallbacksRef = useRef({ onPeriodChange, onSettingsChange })
  useLayoutEffect(() => {
    handleCallbacksRef.current = { onPeriodChange, onSettingsChange }
  })
  // Draw callbacks are stored here because callback-ref hosts (the grid's
  // chart-panel) register them as soon as the handle attaches, which is before
  // the init effect creates the ChartManager. The init effect replays them.
  const drawProgressCbRef = useRef<Parameters<ChartManager['setDrawProgressCallback']>[0]>(null)
  const drawEndCbRef = useRef<Parameters<ChartManager['setDrawEndCallback']>[0]>(null)

  useImperativeHandle(ref, () => {
    // Lazy proxy — defers to managerRef.current at call time so it works
    // even when the manager hasn't been created yet (async init).
    return new Proxy({} as EdgeChartApi, {
      get(_target, prop) {
        // These two must work before the manager exists (see refs above).
        if (prop === 'setDrawProgressCallback') {
          return (cb: Parameters<ChartManager['setDrawProgressCallback']>[0]) => {
            drawProgressCbRef.current = cb
            managerRef.current?.setDrawProgressCallback(cb)
          }
        }
        if (prop === 'setDrawEndCallback') {
          return (cb: Parameters<ChartManager['setDrawEndCallback']>[0]) => {
            drawEndCbRef.current = cb
            managerRef.current?.setDrawEndCallback(cb)
          }
        }
        const manager = managerRef.current
        if (!manager) return undefined
        if (prop === 'setPeriod') {
          return (p: Period) => {
            setPeriod(p)
            manager.setPeriod(p)
            handleCallbacksRef.current.onPeriodChange?.(p)
          }
        }
        if (prop === 'selectTool') {
          // The ONE entry point for arming a drawing tool. Goes through the
          // same `handleSelectTool` the chart's own drawing bar uses, so an
          // external bar (the grid's shared DrawingBar) gets identical
          // behaviour: per-tool default styles, the long/short position
          // routing through `createPositionOverlay`, and the active-tool
          // highlight. Calling `createOverlay` directly skips all three.
          return (name: string) => handleSelectTool(name)
        }
        if (prop === 'startReplay') {
          // Delegate to the lazily-created ReplayController; `replayActive`
          // state + the ReplayControls strip come up off the `replay:started`
          // event this triggers (subscribed below).
          return (startTs: number) => manager.getReplay().enter(startTs)
        }
        if (prop === 'exitReplay') {
          return () => manager.getReplay().exit()
        }
        if (prop === 'stepReplay') {
          return () => manager.getReplay().step()
        }
        if (prop === 'applySettings') {
          // Apply a full ChartSettings snapshot in place. Replaces local
          // settings state, which triggers `useChartStyles` to push the
          // whole bundle to klinecharts in one `chart.setStyles({...})`
          // call — same path as individual modal edits, no remount.
          return (s: ChartSettings) => {
            setSettings(s)
            handleCallbacksRef.current.onSettingsChange?.(s)
          }
        }
        const val = (manager as unknown as Record<string | symbol, unknown>)[prop]
        return typeof val === 'function' ? val.bind(manager) : val
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply a persisted sub-pane height ratio to a live pane, converting the
  // fraction to pixels via the current container height. Guarded so the capture
  // subscription doesn't mistake the programmatic resize for a user drag. No-op
  // when the container hasn't been laid out yet (jsdom / pre-paint).
  const applyPersistedPaneHeight = useCallback((paneId: string, ratio: number) => {
    const manager = managerRef.current
    if (!manager) return
    const total = manager.getContainerHeight()
    if (total <= 0) return
    applyingPaneHeightsRef.current = true
    try {
      manager.setPaneHeight(paneId, ratio * total)
    } finally {
      applyingPaneHeightsRef.current = false
    }
  }, [])

  // Init / destroy. Everything but the datafeed is an initial value; the
  // cleanup is created inside the event, so it closes over this manager.
  const initChart = useEffectEvent(() => {
    if (!chartContainerRef.current) return

    const manager = new ChartManager({
      container: chartContainerRef.current,
      symbol,
      period,
      datafeed,
      theme,
      timezone: settings.timezone,
      extendedHours: settings.extendedHours,
      adjusted: settings.adjusted,
      mainIndicators: initialMainIndicators ?? mainIndicators,
      subIndicators: initialSubIndicators ?? subIndicators,
      initialFocusTimestamp: initialFocusTimestamp ?? undefined,
      anchorVisibleWindow,
      initialBarSpace,
      background: settings.backgroundColor,
      enablePreload,
    })
    managerRef.current = manager
    if (drawProgressCbRef.current) manager.setDrawProgressCallback(drawProgressCbRef.current)
    if (drawEndCbRef.current) manager.setDrawEndCallback(drawEndCbRef.current)
    manager.setToolDeactivatedCallback(() => setActiveTool(null))
    setChartInstance(manager.getChart())
    drawingsRestoredRef.current = false

    // Set background on klinecharts-created container
    const kcContainer = chartContainerRef.current?.firstElementChild as HTMLElement | null
    if (kcContainer) kcContainer.style.background = settings.backgroundColor

    // Seed active indicator state
    const initIndicators: ActiveIndicator[] = []
    let keyCounter = 0
    const mainInds = initialMainIndicators ?? mainIndicators ?? []
    // ChartManager created these (assigning unique aliases to repeated MA-family
    // overlays); zip its instance names back onto our legend rows by position.
    const mainInstanceNames = manager.getMainInstanceNames()
    mainInds.forEach((ind, idx) => {
      const norm = normalizeIndicatorEntry(ind)
      const info = MAIN_INDICATORS.find((i) => i.name === norm.name)
      const effectiveParams = norm.calcParams ?? info?.calcParams
      const visible = norm.visible ?? true
      const rule = norm.timeframeVisibility
      const instanceName = mainInstanceNames[idx] ?? norm.name
      const effVisible = effectiveIndicatorVisible(visible, rule, periodRef.current, norm.name)
      const ai: ActiveIndicator = {
        key: `ind_${++keyCounter}`,
        name: norm.name,
        instanceName,
        label: formatIndicatorLabel(norm.name, effectiveParams, norm.sourceTimeframe),
        paneId: CANDLE_PANE_ID,
        paneType: 'main',
        calcParams: effectiveParams,
        visible,
        styles: norm.styles,
        timeframeVisibility: rule,
        sourceTimeframe: norm.sourceTimeframe,
        hiddenByTimeframe: visible && !effVisible,
      }
      initIndicators.push(ai)
      // Always materialize catalog default styles (layered with any persisted
      // overrides) so the chart matches the settings drawer from first paint.
      {
        const def = getIndicatorDef(norm.name)
        if (def) {
          const styles = buildIndicatorStyles(def, norm.styles)
          manager.updateIndicator(CANDLE_PANE_ID, instanceName, { styles })
        }
      }
      if (!effVisible) {
        manager.updateIndicator(CANDLE_PANE_ID, instanceName, { visible: false })
      }
    })
    const subInds = initialSubIndicators ?? subIndicators ?? []
    // ChartManager may have aliased MTF sub instances (ATR from a higher
    // timeframe); zip its names back by position, like the main loop above.
    const subInstanceNames = manager.getSubInstanceNames()
    subInds.forEach((ind, subIdx) => {
      const norm = normalizeIndicatorEntry(ind)
      const info = SUB_INDICATORS.find((i) => i.name === norm.name)
      const effectiveParams = norm.calcParams ?? info?.calcParams
      const visible = norm.visible ?? true
      const rule = norm.timeframeVisibility
      // VOL can render either in its own sub-pane (default) or as a
      // self-scaled overlay on the candle pane (placement 'main' →
      // VOL_OVERLAY, see registerVolumeIndicator). ChartManager already
      // created the matching instance from the same entry.
      const placement = norm.name === 'VOL' ? (norm.placement ?? 'sub') : undefined
      const isVolOverlay = placement === 'main'
      const instanceName = isVolOverlay ? VOL_OVERLAY_NAME : (subInstanceNames[subIdx] ?? norm.name)
      const paneId = isVolOverlay
        ? CANDLE_PANE_ID
        : (manager.getSubPaneId(instanceName) ?? instanceName)
      const effVisible = effectiveIndicatorVisible(visible, rule, periodRef.current, norm.name)
      const ai: ActiveIndicator = {
        key: `ind_${++keyCounter}`,
        name: norm.name,
        instanceName,
        label: formatIndicatorLabel(norm.name, effectiveParams, norm.sourceTimeframe),
        paneId,
        paneType: 'sub',
        calcParams: effectiveParams,
        visible,
        styles: norm.styles,
        placement,
        timeframeVisibility: rule,
        sourceTimeframe: norm.sourceTimeframe,
        hiddenByTimeframe: visible && !effVisible,
        paneHeightRatio: norm.paneHeightRatio,
      }
      initIndicators.push(ai)
      if (!isVolOverlay) {
        const def = getIndicatorDef(norm.name)
        if (def) {
          const styles = buildIndicatorStyles(def, norm.styles)
          manager.updateIndicator(paneId, instanceName, { styles })
        }
      }
      if (!effVisible) {
        manager.updateIndicator(paneId, instanceName, { visible: false })
      }
    })
    setActiveIndicators(initIndicators)
    // Restore persisted sub-pane heights. ChartManager's constructor created the
    // panes at default ratios; override them here so a reload lands on the last
    // dragged layout. Sub-pane indicators only (skip the price pane + the VOL
    // price-pane overlay, which has no pane of its own).
    for (const ai of initIndicators) {
      if (
        ai.paneType === 'sub' &&
        ai.placement !== 'main' &&
        ai.paneId !== CANDLE_PANE_ID &&
        ai.paneHeightRatio != null
      ) {
        applyPersistedPaneHeight(ai.paneId, ai.paneHeightRatio)
      }
    }
    // Seed the reconciler fingerprint so the first prop-driven reconcile (which
    // fires right after this init) sees an unchanged set and does nothing.
    lastIndicatorSignatureRef.current = indicatorSignature(mainInds, subInds)

    if (onReady) {
      manager.getEventBus().on('chart:ready', onReady)
    }

    return () => {
      manager.destroy()
      managerRef.current = null
      setChartInstance(null)
    }
  })
  useEffect(() => initChart(), [datafeed])

  // Track whether chart is scrolled away from latest bar
  useScrollTracking(
    managerRef,
    setScrollToLatestPos,
    datafeed,
    settings.hideAxisScale,
    settings.priceScalePosition === 'left',
  )

  // Current-bar countdown: price and time left in one pill on the price axis,
  // drawn on the canvas (utils/barCountdownPill.ts) so the crosshair price label
  // paints over it on this chart and on synced peers.
  const barCountdownOn = barCountdownEnabled({
    period,
    hideBarCountdown: settings.hideBarCountdown,
    hideLastPriceLabel: settings.hideLastPriceLabel,
    historical,
  })
  useBarCountdown(managerRef, chartInstance, barCountdownOn)

  // Crosshair sync
  useCrosshairSync(managerRef, crosshairSync, chartId, chartContainerRef, {
    symbol: symbol.ticker,
    ticker: crosshairTickerSync,
    global: crosshairGlobalSync,
  })
  useViewportSync(managerRef, viewportSync, chartId)

  // The host's visible-window callback, read through a ref so a new function
  // each render does not resubscribe.
  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange)
  onVisibleRangeChangeRef.current = onVisibleRangeChange
  const wantsVisibleRange = onVisibleRangeChange != null
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !wantsVisibleRange) return
    const cb = (range: ViewportSyncData) => onVisibleRangeChangeRef.current?.(range)
    manager.subscribeViewport(cb)
    return () => manager.unsubscribeViewport(cb)
  }, [chartInstance, wantsVisibleRange])

  // The host's replay-cursor callback (start and every step), read through a ref.
  const onReplayStepRef = useRef(onReplayStep)
  onReplayStepRef.current = onReplayStep
  const wantsReplaySteps = onReplayStep != null
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !wantsReplaySteps) return
    const bus = manager.getEventBus()
    const report = (e: { cursorTs: number | null } | undefined) => {
      if (e?.cursorTs != null) onReplayStepRef.current?.(e.cursorTs)
    }
    const offStart = bus.on('replay:started', report)
    const offStep = bus.on('replay:step', report)
    return () => {
      offStart()
      offStep()
    }
  }, [chartInstance, wantsReplaySteps])

  // Overlay sync (group + single-panel fallback)
  useOverlaySync(managerRef, overlaySyncGroup, chartId, symbol.ticker, onDrawingEvent)

  // Restore saved drawings on initial load
  const onRestoreDiagnosticRef = useRef(onRestoreDiagnostic)
  onRestoreDiagnosticRef.current = onRestoreDiagnostic
  useDrawingRestore(managerRef, chartInstance, savedDrawings, drawingsRestoredRef, () =>
    onRestoreDiagnosticRef.current?.('ready_missed', { period: periodRef.current.label }),
  )
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return
    return manager
      .getEventBus()
      .on('chart:ready-recovered', (info: { period: string }) =>
        onRestoreDiagnosticRef.current?.('ready_recovered', info),
      )
  }, [chartInstance])

  // Per-bar volume histograms cost payload, so only ask for them while a
  // volume-profile indicator is actually on the chart. Flipping this changes
  // the request URL (and therefore the cache key), so ChartManager reloads —
  // it no-ops when the flag is unchanged, making this safe to run on every
  // indicator change. Ineligible resolutions are filtered in the datafeed, so
  // a daily chart with VPVR on still builds its usual URL.
  // Spec: docs/superpowers/specs/2026-07-28-volume-profile-intraday-granularity-design.md
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return
    manager.setVolumeProfile(
      activeIndicators.some(
        (ind) => ind.name === 'VPVR' || ind.name === 'VPSV' || ind.name === 'VPXD',
      ),
    )
  }, [activeIndicators, chartInstance])

  // TPO is a chart STYLE, not a picker indicator: the profiles replace the
  // candles. So its klinecharts indicator is created and removed here, in step
  // with the chart type, rather than through handleAddIndicator — it must never
  // appear in the indicator list or the legend. Runs on mount too, so a chart
  // restored with `initialChartType: 'tpo'` renders profiles without a toggle.
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    if (chartType === 'tpo') {
      chart.createIndicator({ name: TPO_INDICATOR_NAME }, true, { id: CANDLE_PANE_ID })
    } else {
      chart.removeIndicator(CANDLE_PANE_ID, TPO_INDICATOR_NAME)
    }
  }, [chartType, chartInstance])

  // Heikin Ashi (Beta) is a chart STYLE the same way: its internal indicator
  // paints the averaged candles over the hidden native series. Created WITH its
  // style — useChartStyles' effects are declared earlier and run first, so its
  // overrideIndicator would hit a not-yet-mounted name on the type switch and
  // no-op; later style edits reach it through that override. Keyed on the
  // family so the hollow modifier does not unmount and remount it.
  const heikinActive = styleGroupOf(chartType) === 'heikin'
  const mountHeikin = useEffectEvent(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    if (heikinActive) {
      chart.createIndicator(
        {
          name: HEIKIN_ASHI_INDICATOR_NAME,
          extendData: { heikin: heikinAshiStyleOf(settings, theme, chartType) },
        },
        true,
        { id: CANDLE_PANE_ID },
      )
    } else {
      chart.removeIndicator(CANDLE_PANE_ID, HEIKIN_ASHI_INDICATOR_NAME)
    }
  })
  // Style changes are pushed by useChartStyles; this effect only mounts.
  useEffect(() => {
    mountHeikin()
  }, [heikinActive, chartInstance])

  /**
   * Hand the chart's own bar interval to every HTF projection instance.
   *
   * A klinecharts `draw` receives bars and axes but never the period, and HTF
   * needs it for the one thing that defines the feature: a target is only a
   * HIGHER timeframe relative to the chart's. Without this the projection cannot
   * tell 1H-on-5m (valid) from 1H-on-4H (nothing to project) and would draw a
   * line duplicating the price series.
   *
   * Pushed to each instance by name so stacked projections (HTF, HTF#2 …) all get
   * it — `overrideIndicator` no-ops for names that are not mounted.
   */
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    for (const ind of activeIndicators) {
      const name = ind.instanceName ?? ind.name
      if (!name.startsWith(HTF_INDICATOR_NAME)) continue
      chart.overrideIndicator({
        name,
        extendData: { periodKey: period.label, periodMs: period.ms },
      } as Parameters<typeof chart.overrideIndicator>[0])
    }
  }, [activeIndicators, period, chartInstance])

  const syncSymbol = useEffectEvent(() => {
    managerRef.current?.setSymbol(symbol)
  })
  useEffect(() => {
    syncSymbol()
  }, [symbol.ticker])

  // Bridge the reference-price prop → manager (dashed "today's price" line on
  // anchored charts). Keyed on the scalar fields, not the object identity, so
  // hosts can inline the object literal without re-running the effect every
  // render. `chartInstance` retries once the manager finishes initializing.
  const syncReferencePrice = useEffectEvent(() => {
    managerRef.current?.setReferencePriceLine(referencePrice ?? null)
  })
  useEffect(() => {
    syncReferencePrice()
  }, [chartInstance, referencePrice?.price, referencePrice?.label])

  // Bridge the Pre/Post price prop → manager; scalar-keyed like referencePrice.
  const syncExtendedSessionPrice = useEffectEvent(() => {
    managerRef.current?.setExtendedSessionPrice(extendedSessionPrice ?? null)
  })
  useEffect(() => {
    syncExtendedSessionPrice()
  }, [chartInstance, extendedSessionPrice?.price, extendedSessionPrice?.label])

  // Settings eye: Labels → Pre/post-market price.
  useEffect(() => {
    managerRef.current?.setExtendedSessionMarkVisible(!settings.hideExtendedSessionPrice)
  }, [chartInstance, settings.hideExtendedSessionPrice])

  // Price alert lines (utils/priceAlertLine.ts): data lines the manager owns, so they come
  // back after Remove Drawings and stay up under the drawing-bar eye. The "Alert lines"
  // switch hides them. Keyed on content, like useDilutionLines' levelsKey.
  const priceAlertLinesKey = JSON.stringify(priceAlertLines ?? NO_PRICE_ALERT_LINES)
  const syncPriceAlertLines = useEffectEvent(() => {
    managerRef.current?.setPriceAlertLines(
      settings.showPriceAlertLines !== false
        ? (priceAlertLines ?? NO_PRICE_ALERT_LINES)
        : NO_PRICE_ALERT_LINES,
    )
  })
  // The key stands in for the array.
  useEffect(() => {
    syncPriceAlertLines()
  }, [chartInstance, settings.showPriceAlertLines, priceAlertLinesKey])

  // Cancel on an alert. Read through a ref so a new host closure does not re-wire it.
  const onCancelPriceAlertRef = useRef(onCancelPriceAlert)
  onCancelPriceAlertRef.current = onCancelPriceAlert
  const cancelWired = onCancelPriceAlert != null
  useEffect(() => {
    managerRef.current?.setPriceAlertCancelHandler(
      cancelWired ? (alertId) => onCancelPriceAlertRef.current?.(alertId) : null,
    )
  }, [chartInstance, cancelWired])

  // Bridge the `period` prop → manager when the prop genuinely changes. We
  // compare against the last prop we applied (a ref) rather than internal
  // `period` state: the manager — not the prop — is the source of truth for the
  // displayed resolution (see the 'period:changed' subscription below). If we
  // compared to state, an event-driven state update (per-cell store / embedded
  // toolbar delivering a period the parent never echoed back into the prop)
  // would re-trigger this effect and revert the manager to a stale fallback
  // prop. Gated on `chartInstance` so the sync retries once the manager
  // finishes initializing. Compares by label since Period objects can be
  // re-created with identical content.
  const lastAppliedPropPeriodRef = useRef(initialPeriod.label)
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    if (initialPeriod.label === lastAppliedPropPeriodRef.current) return
    lastAppliedPropPeriodRef.current = initialPeriod.label
    // A change that ORIGINATED in the chart (embedded toolbar, hotkey, the
    // imperative handle) already called manager.setPeriod and echoed the
    // period out via onPeriodChange → host store → this prop. Re-applying it
    // here would fire a second load in the same commit, whose fetch aborts
    // the first's. Only forward what the manager isn't already on.
    if (manager.getPeriod().label === initialPeriod.label) return
    manager.setPeriod(initialPeriod)
  }, [initialPeriod, chartInstance])

  // Range-highlight bridge — shade the [from, to] span (e.g. open->checkpoint) behind the bars.
  // Re-applied on chart (re)create via chartInstance, mirroring the setPeriod bridge above.
  useEffect(() => {
    managerRef.current?.setRangeHighlight(
      rangeHighlight
        ? {
            fromTs: rangeHighlight.from,
            toTs: rangeHighlight.to,
            color: rangeHighlight.color,
            lineColor: rangeHighlight.lineColor,
            label: rangeHighlight.label,
          }
        : null,
    )
  }, [rangeHighlight, chartInstance])

  // Single source of truth for the *displayed* period. Every delivery channel
  // (the prop-bridge above, the imperative setPeriod handle, the embedded
  // toolbar, the quick-switch hotkey) funnels through `manager.setPeriod`,
  // which emits 'period:changed' carrying the resolution it actually loaded.
  // Mirroring that into `period` state guarantees the watermark and
  // useMarketSession reflect the data on screen — never a stale prop fallback.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    return manager.getEventBus().on<{ period: Period }>('period:changed', ({ period: p }) => {
      setPeriod((prev) => (prev.label === p.label ? prev : p))
    })
  }, [chartInstance])

  // A 1D/1W indicator's daily series landed (or changed): re-render so an open
  // settings drawer re-reads its row count for the shortfall cue.
  const [, setMtfSeriesRev] = useState(0)
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    return manager.getEventBus().on('mtf:series', () => setMtfSeriesRev((n) => n + 1))
  }, [chartInstance])

  // Reload veil — mirrors ChartManager's load:pending/load:settled pair (every
  // full reload: symbol swap, focus-date anchor, period switch). While pending,
  // the stale bars kept on screen get dimmed by the overlay below.
  const [reloadVeil, setReloadVeil] = useState(false)
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    const bus = manager.getEventBus()
    const offPending = bus.on('load:pending', () => setReloadVeil(true))
    const offSettled = bus.on('load:settled', () => setReloadVeil(false))
    return () => {
      offPending()
      offSettled()
    }
  }, [chartInstance])

  // Double-click a drawing → the engine fires `overlay:edit`; open the overlay
  // settings drawer over the chart. The event's `styles` is the raw klinecharts
  // OverlayStyle, so we read its `line.{color,size,style}` back into our
  // override shape before handing it to the drawer. Opening the drawer closes
  // every other settings surface (mutual exclusion).
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    return manager.getEventBus().on<{
      id: string
      name: string
      points?: Array<{ timestamp?: number; value?: number }>
      styles?: unknown
      extendData?: unknown
      x?: number
      y?: number
    }>('overlay:edit', (payload) => {
      setEditingIndicatorKey(null)
      setSettingsOpen(false)
      setPriceScaleMenu(null)
      // The drawer always centers over the chart now — no anchor computation.
      setOverlayEdit({
        id: payload.id,
        name: payload.name,
        points: payload.points,
        styles: readOverlayLineStyle(payload.styles),
        extendData: payload.extendData,
      })
    })
  }, [chartInstance])

  // Selection → quick-edit bar. Cleared on deselect so the bar never outlives
  // the drawing it edits.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return
    const bus = manager.getEventBus()
    const offSel = bus.on<{
      id: string
      name: string
      styles?: unknown
      lock: boolean
      points?: Array<{ timestamp?: number; value?: number }>
      extendData?: unknown
    }>('overlay:selected', (p) => {
      setQuickBar({
        id: p.id,
        name: p.name,
        // Already an app-level OverlayStyleOverride (raw hex) — running it back
        // through `readOverlayLineStyle` would re-derive it from klinecharts'
        // resolved colours and lose the un-composed hex.
        styles: p.styles as OverlayStyleOverride | undefined,
        locked: p.lock,
        points: p.points,
        extendData: p.extendData,
      })
    })
    const offDesel = bus.on('overlay:deselected', () => setQuickBar(null))
    return () => {
      offSel?.()
      offDesel?.()
    }
  }, [chartInstance])

  // Position-plan % editor: a click on an entry's label pill (relayed by
  // ChartManager as plan:weightEdit) opens a preset menu anchored at that
  // entry line's pixel y. Left position resolves at render (overlayLeft).
  const [planWeightEdit, setPlanWeightEdit] = useState<{
    request: PlanWeightEditRequest
    top: number
  } | null>(null)
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return
    const bus = manager.getEventBus()
    return bus.on<PlanWeightEditRequest>('plan:weightEdit', (request) => {
      const px = chartInstance.convertToPixel(
        { value: request.entryPrice },
        { paneId: CANDLE_PANE_ID },
      )
      const y = (Array.isArray(px) ? px[0] : px) as { y?: number } | null
      setPlanWeightEdit({
        request,
        top: typeof y?.y === 'number' && isFinite(y.y) ? y.y + 10 : 60,
      })
    })
  }, [chartInstance])

  // Annotation text box: a blank Annotation was just placed (relayed by
  // ChartManager as annotation:textEdit, anchor in container pixels). A ticker
  // swap sweeps the unsaved drawing, so the box closes with it.
  const [annotationEdit, setAnnotationEdit] = useState<AnnotationTextEditRequest | null>(null)
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    const bus = manager.getEventBus()
    const offEdit = bus.on<AnnotationTextEditRequest>('annotation:textEdit', setAnnotationEdit)
    const offCleared = bus.on('overlays:cleared', () => setAnnotationEdit(null))
    return () => {
      offEdit()
      offCleared()
    }
  }, [chartInstance])

  // Shift+click on a daily bar asks the host to drill into that day's intraday
  // view. The engine only detects the gesture; the host (chart panel) owns the
  // date-picker state + period switch so the whole panel reloads cohesively.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    // Only arm the Alt-hold highlight + gesture when the host actually wired a
    // drill handler — otherwise the yellow candle cues a dead action (e.g. in
    // chart-scanner grid cells, which don't drill on day-click).
    manager.setIntradayDrillEnabled(!!onIntradayDrill)
    return manager.getEventBus().on<{
      dayTimestamp: number
    }>('intradayDrill:requested', ({ dayTimestamp }) => onIntradayDrill?.(dayTimestamp))
  }, [chartInstance, onIntradayDrill])

  // Bar-replay lifecycle: mirror the engine's start/end events into local
  // `replayActive` state (drives the ReplayControls strip + Escape handler) and
  // notify the host so it can frame the mosaic window with the replay border.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    const bus = manager.getEventBus()
    const offStart = bus.on('replay:started', () => {
      setReplayActive(true)
      onReplayActiveChangeRef.current?.(true)
    })
    const offEnd = bus.on('replay:ended', () => {
      setReplayActive(false)
      setReplayLoopArming(false)
      onReplayActiveChangeRef.current?.(false)
    })
    // Loop-selection arming (the strip's loop button latches while the two
    // range clicks are being placed). Cleared by start (selection became a
    // loop) and ended (cancelled / stopped) alike.
    const offLoopArmed = bus.on('replay:loop-armed', () => setReplayLoopArming(true))
    const offLoopStarted = bus.on('replay:loop-started', () => setReplayLoopArming(false))
    const offLoopEnded = bus.on('replay:loop-ended', () => setReplayLoopArming(false))
    return () => {
      offStart()
      offEnd()
      offLoopArmed()
      offLoopStarted()
      offLoopEnded()
    }
  }, [chartInstance])

  // Escape layering during replay: while ACTIVE and PLAYING, Escape pauses and
  // consumes (capture-phase, preventDefault + stopPropagation — same pattern as
  // ChartManager.setupEscapeDrawCancel), so it never reaches the maximize-collapse
  // handler. While active but PAUSED it neither exits nor consumes — it falls
  // through to normal layering. Exit is only via the ✕ chip.
  useEffect(() => {
    if (!replayActive) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // A text field (the annotation box) owns its Escape: consuming it here,
      // in capture, would stop it before the field ever saw it.
      const t = e.target as HTMLElement | null
      const tag = t?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return
      const replay = managerRef.current?.getReplay()
      if (!replay?.getState().playing) return
      replay.pause()
      e.preventDefault()
      e.stopPropagation()
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [replayActive])

  // Bridge the `initialChartType` prop → internal chartType state. Like period,
  // chartType is seeded once via useState; when a parent (the page-level shared
  // toolbar) drives chart type externally, this effect applies it in place
  // instead of forcing a remount. useChartStyles re-runs on the state change and
  // pushes `candle.type` through setStyles.
  useEffect(() => {
    if (initialChartType === undefined) return
    if (initialChartType === chartType) return
    setChartType(initialChartType)
  }, [initialChartType, chartType])

  // Bridge the `initialMainIndicators` / `initialSubIndicators` props → live
  // chart, reconciling in place (create / remove / update) instead of forcing a
  // remount. This is the indicator half of the controlled-surface refactor: the
  // page-level shared toolbar owns indicator config and pushes it down as props;
  // here we diff it against the mounted set and apply only the delta.
  //
  // Only runs when at least one of the props is defined — the embedded-toolbar
  // (single-chart) path leaves both undefined and owns indicators through its
  // own handlers (handleAddIndicator etc.), so we must not fight it. We never
  // call `notifyIndicatorsChanged` here: the parent is the source of truth, and
  // echoing back would loop. Matching is positional per (paneType, name).
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    if (initialMainIndicators === undefined && initialSubIndicators === undefined) return

    const desiredMain = initialMainIndicators ?? []
    const desiredSub = initialSubIndicators ?? []
    const signature = indicatorSignature(desiredMain, desiredSub)
    if (signature === lastIndicatorSignatureRef.current) return

    // Placement is part of the identity key: a VOL placement flip can't be
    // applied in place (different registered indicator + different pane), so
    // keying on it routes the change through remove + recreate.
    // `sourceTimeframe` is part of the identity on purpose: switching an
    // instance between the built-in template (no timeframe) and an MTF clone
    // alias needs a re-create, and folding it into the key routes any change
    // through the remove+add diff below instead of a patch that can't work.
    const occKey = (
      paneType: 'main' | 'sub',
      name: string,
      placement: 'main' | 'sub' | undefined,
      occ: number,
      sourceTimeframe?: string,
    ) => `${paneType}:${name}:${placement ?? ''}:${occ}:${sourceTimeframe ?? ''}`

    // Index the currently-mounted indicators by their positional occurrence key.
    const currentByKey = new Map<string, ActiveIndicator>()
    {
      const counts = new Map<string, number>()
      for (const ind of activeIndicatorsRef.current) {
        const ck = `${ind.paneType}:${ind.name}`
        const occ = counts.get(ck) ?? 0
        counts.set(ck, occ + 1)
        currentByKey.set(
          occKey(ind.paneType, ind.name, ind.placement, occ, ind.sourceTimeframe),
          ind,
        )
      }
    }

    // Flatten desired props into an ordered occurrence list.
    type Desired = {
      key: string
      paneType: 'main' | 'sub'
      name: string
      calcParams?: unknown[]
      styles?: IndicatorStyleOverrides
      visible: boolean
      placement?: 'main' | 'sub'
      timeframeVisibility?: TimeframeVisibilityRule
      sourceTimeframe?: string
      paneHeightRatio?: number
    }
    const desired: Desired[] = []
    {
      const counts = new Map<string, number>()
      const collect = (paneType: 'main' | 'sub', entries: IndicatorEntry[]) => {
        for (const raw of entries) {
          const norm = normalizeIndicatorEntry(raw)
          const ck = `${paneType}:${norm.name}`
          const occ = counts.get(ck) ?? 0
          counts.set(ck, occ + 1)
          const placement = norm.name === 'VOL' ? (norm.placement ?? 'sub') : undefined
          desired.push({
            key: occKey(paneType, norm.name, placement, occ, norm.sourceTimeframe),
            paneType,
            name: norm.name,
            calcParams: norm.calcParams,
            styles: norm.styles,
            visible: norm.visible ?? true,
            placement,
            timeframeVisibility: norm.timeframeVisibility,
            sourceTimeframe: norm.sourceTimeframe,
            paneHeightRatio: norm.paneHeightRatio,
          })
        }
      }
      collect('main', desiredMain)
      collect('sub', desiredSub)
    }
    const desiredKeys = new Set(desired.map((d) => d.key))

    // Remove indicators that no longer appear in the desired set.
    for (const [key, ind] of currentByKey) {
      if (!desiredKeys.has(key)) {
        manager.removeIndicator(ind.paneId, ind.instanceName ?? ind.name)
      }
    }

    // Re-build the legend in desired order, adding new and updating changed.
    const usedMain = new Map<string, Set<string>>()
    const getUsed = (name: string) => {
      let s = usedMain.get(name)
      if (!s) {
        s = new Set()
        usedMain.set(name, s)
      }
      return s
    }
    const nextActive: ActiveIndicator[] = []
    // Price-pane indicators this reconcile CREATES (a host-side add or template
    // load). Never set by the first reconcile after init, which is a no-op.
    const newCandleKeys = new Set<string>()
    let keyCounter = 0

    for (const d of desired) {
      const info = (d.paneType === 'main' ? MAIN_INDICATORS : SUB_INDICATORS).find(
        (i) => i.name === d.name,
      )
      const def = getIndicatorDef(d.name)
      const existing = currentByKey.get(d.key)

      if (existing) {
        const instanceName = existing.instanceName ?? existing.name
        if (d.paneType === 'main') getUsed(d.name).add(instanceName)

        const calcChanged =
          JSON.stringify(existing.calcParams ?? null) !== JSON.stringify(d.calcParams ?? null)
        const stylesChanged =
          JSON.stringify(existing.styles ?? null) !== JSON.stringify(d.styles ?? null)
        // Compare EFFECTIVE visibility (manual eye AND the timeframe rule on the
        // current period): a rule edit that changes nothing on this period is a
        // no-op on the chart, and a manual toggle only applies if the rule allows.
        const existingEffective = effectiveIndicatorVisible(
          existing.visible,
          existing.timeframeVisibility,
          periodRef.current,
          existing.name,
        )
        const desiredEffective = effectiveIndicatorVisible(
          d.visible,
          d.timeframeVisibility,
          periodRef.current,
          d.name,
        )
        const effectiveChanged = existingEffective !== desiredEffective

        if (calcChanged || stylesChanged || effectiveChanged) {
          const patch: Parameters<typeof manager.updateIndicator>[2] = {}
          if (calcChanged) patch.calcParams = d.calcParams
          if (stylesChanged && def && d.styles) patch.styles = buildIndicatorStyles(def, d.styles)
          if (effectiveChanged) patch.visible = desiredEffective
          manager.updateIndicator(existing.paneId, instanceName, patch)
        }

        // Restore a persisted sub-pane height without re-creating the indicator.
        // Only touch the pane when the target ratio genuinely differs from the
        // live one (epsilon guard) — on the chart that produced the drag the two
        // already match, so this is a no-op there and applies once on siblings.
        let paneHeightRatio = existing.paneHeightRatio
        if (d.paneType === 'sub' && d.placement !== 'main' && d.paneHeightRatio != null) {
          paneHeightRatio = d.paneHeightRatio
          const currentRatio = manager.getPaneHeightRatio(existing.paneId)
          if (
            currentRatio == null ||
            Math.abs(currentRatio - d.paneHeightRatio) > PANE_HEIGHT_RATIO_EPSILON
          ) {
            applyPersistedPaneHeight(existing.paneId, d.paneHeightRatio)
          }
        }

        const effectiveParams = d.calcParams ?? info?.calcParams
        nextActive.push({
          ...existing,
          calcParams: effectiveParams,
          styles: d.styles,
          visible: d.visible,
          timeframeVisibility: d.timeframeVisibility,
          sourceTimeframe: d.sourceTimeframe,
          hiddenByTimeframe: d.visible && !desiredEffective,
          paneHeightRatio,
          label: formatIndicatorLabel(d.name, effectiveParams, d.sourceTimeframe),
        })
        continue
      }

      // New indicator — mirror handleAddIndicator's MA-family aliasing + palette,
      // but self-contained (no notify, batched into one setActiveIndicators).
      const effectiveParams = d.calcParams ?? info?.calcParams
      let instanceName = d.name
      let effectiveStyles = d.styles
      // An MTF instance must be a clone alias even as the first of its name —
      // the built-in template's calc ignores extendData.timeframe.
      const wantsMtf = !!d.sourceTimeframe && isMtfEligibleIndicator(d.name)
      const mtfExtend = wantsMtf ? { timeframe: d.sourceTimeframe } : undefined
      if (d.paneType === 'main' && isDuplicableIndicator(d.name)) {
        const used = getUsed(d.name)
        instanceName = allocateInstanceName(d.name, wantsMtf ? new Set([...used, d.name]) : used)
        ensureInstanceRegistered(instanceName, d.name)
        if (instanceName !== d.name && def && !effectiveStyles && !wantsMtf) {
          const color = DUP_PALETTE[used.size % DUP_PALETTE.length]
          effectiveStyles = { [def.figures[0].key]: { color } }
        }
      } else if (d.paneType === 'sub' && wantsMtf && isDuplicableIndicator(d.name)) {
        instanceName = allocateInstanceName(
          d.name,
          new Set([...activeIndicatorsRef.current.map((i) => i.instanceName ?? i.name), d.name]),
        )
        ensureInstanceRegistered(instanceName, d.name)
      }
      if (d.paneType === 'main') getUsed(d.name).add(instanceName)

      // VOL with placement 'main' overlays the candle pane via its
      // VOL_OVERLAY variant instead of spawning a sub-pane.
      const isVolOverlay = d.name === 'VOL' && d.placement === 'main'
      if (isVolOverlay) instanceName = VOL_OVERLAY_NAME
      const onCandlePane = d.paneType === 'main' || isVolOverlay
      let paneId: string
      if (onCandlePane) {
        paneId =
          manager.createIndicator(instanceName, true, CANDLE_PANE_ID, effectiveParams, mtfExtend) ??
          CANDLE_PANE_ID
      } else {
        paneId =
          manager.createIndicator(instanceName, false, undefined, effectiveParams, mtfExtend) ??
          instanceName
      }

      const targetPaneId = onCandlePane ? CANDLE_PANE_ID : paneId
      if (def && !isVolOverlay) {
        manager.updateIndicator(targetPaneId, instanceName, {
          styles: buildIndicatorStyles(def, effectiveStyles),
        })
      }
      const desiredEffective = effectiveIndicatorVisible(
        d.visible,
        d.timeframeVisibility,
        periodRef.current,
        d.name,
      )
      if (!desiredEffective) {
        manager.updateIndicator(targetPaneId, instanceName, { visible: false })
      }

      // Newly-created sub pane: apply its persisted height straight away (same
      // restore path as init, but for an indicator added via prop reconcile).
      if (d.paneType === 'sub' && !isVolOverlay && d.paneHeightRatio != null) {
        applyPersistedPaneHeight(targetPaneId, d.paneHeightRatio)
      }

      const reconKey = `ind_recon_${++keyCounter}_${Math.random().toString(36).slice(2, 6)}`
      if (targetPaneId === CANDLE_PANE_ID) newCandleKeys.add(reconKey)
      nextActive.push({
        key: reconKey,
        name: d.name,
        instanceName,
        label: formatIndicatorLabel(d.name, effectiveParams, d.sourceTimeframe),
        paneId: targetPaneId,
        paneType: d.paneType,
        calcParams: effectiveParams,
        visible: d.visible,
        styles: effectiveStyles,
        placement: d.placement,
        sourceTimeframe: d.sourceTimeframe,
        timeframeVisibility: d.timeframeVisibility,
        hiddenByTimeframe: d.visible && !desiredEffective,
        paneHeightRatio: d.paneHeightRatio,
      })
    }

    lastIndicatorSignatureRef.current = signature
    // Added while the eye hides indicators: show the new ones, the rest go
    // individually off (same rule as a legend click).
    if (newCandleKeys.size > 0 && manager.areIndicatorsHidden()) {
      releaseIndicatorHideRef.current(nextActive, newCandleKeys)
      return
    }
    setActiveIndicators(nextActive)
  }, [initialMainIndicators, initialSubIndicators, chartInstance, applyPersistedPaneHeight])

  // Re-apply per-timeframe visibility whenever the period changes. Only touches
  // indicators that carry a rule — explicit on the entry OR built-in for the
  // indicator type (VWAP on daily+) — everything else is period-agnostic.
  // The manual eye state (`visible`) is never mutated here; we only flip the
  // effective on-chart visibility and the derived `hiddenByTimeframe` cue.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    const cur = activeIndicatorsRef.current
    const hasRule = (i: ActiveIndicator) =>
      (i.timeframeVisibility ?? BUILT_IN_TIMEFRAME_VISIBILITY[i.name]) !== undefined
    if (!cur.some(hasRule)) return
    let changed = false
    const next = cur.map((ind) => {
      if (!hasRule(ind)) return ind
      const effVisible = effectiveIndicatorVisible(
        ind.visible,
        ind.timeframeVisibility,
        period,
        ind.name,
      )
      manager.updateIndicator(ind.paneId, ind.instanceName ?? ind.name, { visible: effVisible })
      const hidden = ind.visible !== false && !effVisible
      if ((ind.hiddenByTimeframe ?? false) !== hidden) {
        changed = true
        return { ...ind, hiddenByTimeframe: hidden }
      }
      return ind
    })
    if (changed) setActiveIndicators(next)
  }, [period])

  // Keep ChartManager's "timeframe-suppressed" set current: the instances the
  // timeframe rule hides on THIS period. The global show/hide ("S" hotkey / eye)
  // reads it so showing never overrides an indicator's per-timeframe visibility.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    const suppressed = activeIndicators
      .filter((ind) => !effectiveIndicatorVisible(true, ind.timeframeVisibility, period, ind.name))
      .map((ind) => ind.instanceName ?? ind.name)
    manager.setTimeframeSuppressed(suppressed)
  }, [activeIndicators, period])

  // Canvas tooltip policy — BOTH rows are React overlays here, so neither
  // canvas row may draw:
  //   - candle → OhlcHeader (titles light, values bold; canvas text carries a
  //     single weight for the whole row)
  //   - indicator → IndicatorLegend (values + hover controls)
  // Re-asserted on every theme change: `ChartManager.setTheme` hands a theme
  // OBJECT straight to `setStyles`, so a bundle that omits `showRule` silently
  // restores the klinecharts default ('always') and double-prints over the
  // React rows.
  useEffect(() => {
    const chart = managerRef.current?.getChart()
    if (!chart) return
    chart.setStyles({
      candle: {
        tooltip: { showRule: 'none' },
      },
      indicator: {
        tooltip: { showRule: 'none' },
      },
    } as Parameters<typeof chart.setStyles>[0])
  }, [chartInstance, theme, themeVersion])

  // What the settings modal's color swatches must show. Resolved from the SAME
  // theme bundle useChartStyles paints from (`applyTheme` handles the 'dark' /
  // 'light' string forms), so a swatch can never display a color that isn't on
  // the canvas. Keyed on themeVersion so a same-mode preset switch re-resolves.
  const settingsThemeColors = useMemo(
    () => resolveSettingsThemeColors(theme, settings, themeVersion),
    [theme, settings, themeVersion],
  )

  // Theme-resolved candle colours for React chrome (the OHLC header). Passing
  // the RAW stored settings there bypassed the theme system: the header never
  // matched the canvas candles, and a reset (which clears customizedColors,
  // not the stored fallback) visibly didn't take.
  const effectiveCandleUp = effectiveColor(
    'candleUpColor',
    settings.candleUpColor,
    settingsThemeColors,
    settings.customizedColors,
  )
  const effectiveCandleDown = effectiveColor(
    'candleDownColor',
    settings.candleDownColor,
    settingsThemeColors,
    settings.customizedColors,
  )
  // The "+" by the crosshair price label wears the label's own resolved colour.
  const effectiveCrosshairLabelBg = effectiveColor(
    'crosshairLabelBg',
    settings.crosshairLabelBg,
    settingsThemeColors,
    settings.customizedColors,
  )

  // Resize observer
  useChartResize(chartContainerRef, managerRef)

  // Right-click on the price-axis strip opens the fast-path context menu.
  // ChartManager.priceAxisPaneAt detects a viewport point over the axis; when
  // it's non-null we preventDefault (suppress the native menu) and open ours at
  // the cursor. Attached to the same container ChartManager owns.
  const onAddPriceAlertRef = useRef(onAddPriceAlert)
  onAddPriceAlertRef.current = onAddPriceAlert
  useEffect(() => {
    const el = chartContainerRef.current
    if (!el) return
    // klinecharts aborts a half-placed drawing on the button-2 mousedown (KC:12328-12332),
    // which clears the pending draw before the contextmenu below. Record it first (capture
    // phase, ahead of klinecharts' own listeners) so that right click is not also an alert
    // cancel.
    let abortedDrawing = false
    const markAbort = (e: MouseEvent) => {
      abortedDrawing = e.button === 2 && managerRef.current?.isDrawingPending() === true
    }
    const handle = (e: MouseEvent) => {
      const manager = managerRef.current
      if (!manager) return
      e.preventDefault()
      const aborted = abortedDrawing
      abortedDrawing = false
      // A right click (or a touch long press) on an alert line or its label cancels it, and
      // no menu opens (spec C3). Returns false at once when the chart has no alert lines. A
      // user's drawing under the pointer wins: the click is the drawing's (its menu), and the
      // alert stays; so does a right click that just aborted a drawing.
      if (
        !aborted &&
        !manager.isUserDrawingHovered() &&
        manager.cancelPriceAlertAt(e.clientX, e.clientY)
      )
        return
      // Only a chart that offers price alerts reads the price (and ChartManager's
      // DomPosition.Main, which the test harnesses do not mock).
      const alertPrice = onAddPriceAlertRef.current ? manager.alertPriceAt(e.clientY) : null
      if (manager.priceAxisPaneAt(e.clientX, e.clientY) != null) {
        // Over the price-axis strip → the price-scale fast-path menu.
        setSettingsOpen(false)
        setBodyMenu(null)
        setPriceScaleMenu({ x: e.clientX, y: e.clientY, alertPrice })
        return
      }
      // Over the chart body → the body menu, scoped to the trading day of the
      // bar under the cursor (same source the Alt+click drill reads).
      const ts = manager.cursorBarTimestamp()
      const dayTs = ts != null ? alignTimestamp(ts, PERIODS['1D']) : null
      setSettingsOpen(false)
      setPriceScaleMenu(null)
      setBodyMenu({ x: e.clientX, y: e.clientY, dayTs, barTs: ts, alertPrice })
    }
    el.addEventListener('mousedown', markAbort, true)
    el.addEventListener('contextmenu', handle)
    return () => {
      el.removeEventListener('mousedown', markAbort, true)
      el.removeEventListener('contextmenu', handle)
    }
  }, [chartInstance])

  // Floating-overlay insets. The glass toolbar (top) and drawing bar (left)
  // hover over the canvas, so the React overlays (legend, pills, sort badge)
  // shift to clear them. Zero when absent.
  const topInset = toolbarVisibleProp ? TOOLBAR_OVERLAY_INSET : 0
  const drawingBarAvailable = toolbarVisibleProp || drawingToolsEnabled
  // Top edge of the EXPANDED drawing bar, measured. The bar hangs from
  // FLOAT_ANCHOR_TOP_PCT and grows UPWARD, so how far up it reaches depends on
  // both the chart's height and how many tools are in it — neither of which can
  // be assumed. `null` until first measured.
  const [drawingBarTop, setDrawingBarTop] = useState<number | null>(null)
  const measureDrawingBar = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    setDrawingBarTop(el.offsetTop)
  }, [])

  // Measured bottom edge of the floating toolbar strip (relative to the chart
  // area). The OHLC header rides INSIDE the strip as a wrapping flex item, so
  // the strip grows a row when the panel is narrow — the info stack below
  // (legend, sort badge, pills) and the drawing bar follow the live height.
  const toolbarRootRef = useRef<HTMLDivElement | null>(null)
  // Pre-measurement estimate matched to the strip's contents — with the
  // control chips hidden the strip is just the 16px OHLC row (top 6 + 16),
  // vs ~28px with chips. A mismatched guess made the legend visibly hop a
  // few px once the ResizeObserver's first real measurement landed.
  const [toolbarBottom, setToolbarBottom] = useState(
    toolbarControlsVisible ? TOOLBAR_OVERLAY_INSET - 6 : 22,
  )
  useEffect(() => {
    if (!toolbarVisibleProp) return
    const el = toolbarRootRef.current
    if (!el) return
    const measure = () => {
      const h = el.offsetHeight
      // jsdom (and pre-layout frames) report 0 — keep the fallback estimate.
      if (h > 0) setToolbarBottom(el.offsetTop + h)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [toolbarVisibleProp])

  // All visual style application (theme, settings, fixed price axis)
  useChartStyles(
    managerRef,
    chartContainerRef,
    chartInstance,
    theme,
    themeVersion,
    styles,
    settings,
    chartType,
    period,
    globalSettings,
    interactionSettings,
    setSettings,
    // Countdown pill on: hide the native tag text (see useChartStyles).
    barCountdownOn,
  )

  /* ---- Market session display (highlights, extended hours, previous close) ---- */
  useMarketSession(managerRef, chartInstance, period, settings)

  /* ---- Earnings markers (system indicator + hover tooltip) ---- */
  useEarningsMarkers(chartInstance, settings, earnings)
  useSplitMarkers(chartInstance, settings, splits)
  useDilutionLines(chartInstance, settings, dilutionLevels)

  /* ---- Toolbar callbacks ---- */

  const handlePeriodChange = useCallback(
    (p: Period) => {
      setPeriod(p)
      managerRef.current?.setPeriod(p)
      onPeriodChange?.(p)
    },
    [onPeriodChange],
  )

  // Type a number while hovering the chart to quick-switch the interval (Enter applies).
  const applyPeriodKey = useCallback(
    (key: PeriodKey) => handlePeriodChange(PERIODS[key]),
    [handlePeriodChange],
  )
  const intervalSwitch = useIntervalQuickSwitch(chartContainerRef, applyPeriodKey)

  // Video-player key layer while replay is active: Space play/pause,
  // ArrowRight step, ArrowUp/Down speed, Escape exit (last in the chain).
  const getReplayController = useCallback(() => managerRef.current?.getReplay() ?? null, [])
  useReplayHotkeys(chartContainerRef, getReplayController, replayActive)

  const handleChartTypeChange = useCallback(
    (type: string) => {
      setChartType(type)
      onChartTypeChangeProp?.(type)
      managerRef.current?.getChart()?.setStyles({
        candle: candleStylesForChartType(type),
      } as Parameters<ReturnType<ChartManager['getChart']>['setStyles']>[0])
    },
    [onChartTypeChangeProp],
  )

  // Notify parent of indicator changes — called directly from mutation callbacks,
  // not through a useEffect. No render cycle delay, no skip-initial guard needed.
  const notifyIndicatorsChanged = useCallback(
    (next: ActiveIndicator[]) => {
      if (!onIndicatorsChange) return
      const emit = (i: ActiveIndicator) => {
        const hasStyles = i.styles && Object.keys(i.styles).length > 0
        const hasVisibilityOverride = i.visible === false
        // 'sub' is the default placement, so only a 'main' overlay needs persisting.
        const hasPlacementOverride = i.placement === 'main'
        const hasTimeframeRule = !!i.timeframeVisibility
        const hasSourceTimeframe = !!i.sourceTimeframe
        // Only sub-pane indicators carry a meaningful height ratio.
        const hasPaneHeight =
          i.paneHeightRatio != null && i.paneType === 'sub' && i.placement !== 'main'
        if (
          !i.calcParams &&
          !hasStyles &&
          !hasVisibilityOverride &&
          !hasPlacementOverride &&
          !hasTimeframeRule &&
          !hasSourceTimeframe &&
          !hasPaneHeight
        )
          return i.name
        const entry: {
          name: string
          calcParams?: unknown[]
          styles?: ActiveIndicator['styles']
          visible?: boolean
          placement?: 'main' | 'sub'
          timeframeVisibility?: TimeframeVisibilityRule
          sourceTimeframe?: string
          paneHeightRatio?: number
        } = { name: i.name }
        if (i.calcParams) entry.calcParams = i.calcParams
        if (hasStyles) entry.styles = i.styles
        if (hasVisibilityOverride) entry.visible = false
        if (hasPlacementOverride) entry.placement = 'main'
        if (hasTimeframeRule) entry.timeframeVisibility = i.timeframeVisibility
        if (hasSourceTimeframe) entry.sourceTimeframe = i.sourceTimeframe
        if (hasPaneHeight) entry.paneHeightRatio = i.paneHeightRatio
        return entry
      }
      const main = next.filter((i) => i.paneType === 'main').map(emit)
      const sub = next.filter((i) => i.paneType === 'sub').map(emit)
      onIndicatorsChange(main, sub)
    },
    [onIndicatorsChange],
  )
  // Latest notify fn for the capture subscription below (kept in a ref so the
  // subscription itself only re-runs on chartInstance, never on callback churn —
  // otherwise the in-flight debounce timer would be dropped mid-drag).
  const notifyIndicatorsChangedRef = useRef(notifyIndicatorsChanged)
  notifyIndicatorsChangedRef.current = notifyIndicatorsChanged

  // Push each price-pane row's OWN effective visibility (its eye AND its
  // timeframe rule) to the engine. The global show reveals every instance, so
  // this re-hides the rows switched off individually.
  const applyIndicatorVisibility = useCallback((list: ActiveIndicator[]) => {
    const manager = managerRef.current
    if (!manager) return
    for (const ind of list) {
      if (ind.paneId !== CANDLE_PANE_ID) continue
      manager.updateIndicator(ind.paneId, ind.instanceName ?? ind.name, {
        visible: effectiveIndicatorVisible(
          ind.visible,
          ind.timeframeVisibility,
          periodRef.current,
          ind.name,
        ),
      })
    }
  }, [])

  // Turning one indicator on (or adding one) while the eye hides indicators:
  // clear the global hide, show the kept rows, and leave every other price-pane
  // row individually OFF (Chris, 2026-09-25). `report` is false when a grid
  // applies this to sibling cells, whose shared eye it already cleared.
  const releaseIndicatorHide = useCallback(
    (list: ActiveIndicator[], keep: ReadonlySet<string>, report = true) => {
      const manager = managerRef.current
      if (!manager) return
      const next = list.map((i) => {
        if (i.paneId !== CANDLE_PANE_ID) return i
        const visible = keep.has(i.key)
        const eff = effectiveIndicatorVisible(
          visible,
          i.timeframeVisibility,
          periodRef.current,
          i.name,
        )
        return { ...i, visible, hiddenByTimeframe: visible && !eff }
      })
      activeIndicatorsRef.current = next
      manager.setIndicatorsVisible(true)
      applyIndicatorVisibility(next)
      setActiveIndicators(next)
      notifyIndicatorsChangedRef.current(next)
      const nextHidden = { ...hiddenStateRef.current, indicators: false }
      hiddenStateRef.current = nextHidden
      setHiddenState(nextHidden)
      if (report) onHiddenStateChangeRef.current?.(nextHidden)
    },
    [applyIndicatorVisibility],
  )
  const releaseIndicatorHideRef = useRef(releaseIndicatorHide)
  releaseIndicatorHideRef.current = releaseIndicatorHide

  // Mirror the engine's hide flag; on a SHOW, re-hide individually-off rows.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    setIndicatorsEyeHidden(manager.areIndicatorsHidden())
    const bus = manager.getEventBus()
    const offHidden = bus.on('indicators:hidden', (hidden: boolean) => {
      setIndicatorsEyeHidden(hidden)
      if (!hidden) applyIndicatorVisibility(activeIndicatorsRef.current)
    })
    // A grid's shared eye was cleared from another cell: convert this chart
    // silently (the grid already persisted its own eye).
    const offRelease = bus.on('indicators:releaseRequested', () => {
      releaseIndicatorHideRef.current(activeIndicatorsRef.current, new Set(), false)
    })
    return () => {
      offHidden()
      offRelease()
    }
  }, [chartInstance, applyIndicatorVisibility])

  // Capture sub-pane separator drags → persist each sub indicator's height as a
  // container-relative ratio. OnPaneDrag fires continuously, so we debounce to
  // the drag's settle point, then merge the live ratios into the current entries
  // and report upward through the same onIndicatorsChange channel the host
  // already persists (add/remove/style edits use it too). Round to 3 decimals so
  // sub-pixel jitter doesn't churn the persisted value or the diff signature.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const capture = () => {
      const totalH = manager.getContainerHeight()
      if (totalH <= 0) return
      let changed = false
      const next = activeIndicatorsRef.current.map((ind) => {
        if (ind.paneType !== 'sub' || ind.placement === 'main' || ind.paneId === CANDLE_PANE_ID)
          return ind
        const h = manager.getPaneHeight(ind.paneId)
        if (h == null || h <= 0) return ind
        const ratio = Math.round((h / totalH) * 1000) / 1000
        if (
          ind.paneHeightRatio != null &&
          Math.abs(ind.paneHeightRatio - ratio) < PANE_HEIGHT_RATIO_EPSILON
        )
          return ind
        changed = true
        return { ...ind, paneHeightRatio: ratio }
      })
      if (!changed) return
      setActiveIndicators(next)
      notifyIndicatorsChangedRef.current(next)
    }
    const onDrag = () => {
      // Ignore heights we pushed ourselves (restore path) — only real user drags
      // should persist. See applyPersistedPaneHeight / applyingPaneHeightsRef.
      if (applyingPaneHeightsRef.current) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(capture, PANE_HEIGHT_CAPTURE_DEBOUNCE_MS)
    }
    const unsub = manager.subscribeOnPaneDrag(onDrag)
    return () => {
      if (timer) clearTimeout(timer)
      unsub()
    }
  }, [chartInstance])

  const handleAddIndicator = useCallback(
    (name: string, paneType: 'main' | 'sub', calcParams?: unknown[]) => {
      // Layer user-saved defaults on top of the catalog defaults coming from
      // the Toolbar picker. Without this, a user who's set MA(50) as their
      // default still gets MA(20) every time they click MA.
      const layered = layerIndicatorUserDefault(name, calcParams)
      const effectiveParams = layered.calcParams
      // User-saved default rule wins; otherwise a fresh add stamps the built-in
      // catalog rule (e.g. VWAP hidden on daily+) into the persisted entry.
      const rule = layered.timeframeVisibility ?? BUILT_IN_TIMEFRAME_VISIBILITY[name]
      const def = getIndicatorDef(name)

      // Repeated MA-family overlays on the main pane each get their own
      // registered name + a distinct default color so they coexist as
      // independent rows instead of overriding one another.
      let instanceName = name
      let effectiveStyles = layered.styles
      if (paneType === 'main' && isDuplicableIndicator(name)) {
        const used = new Set(
          activeIndicatorsRef.current
            .filter((i) => i.paneType === 'main' && i.name === name)
            .map((i) => i.instanceName ?? i.name),
        )
        instanceName = allocateInstanceName(name, used)
        ensureInstanceRegistered(instanceName, name)
        // Auto-assign a distinct line color to the 2nd+ instance (unless the
        // user already has a saved style default for this indicator type).
        if (instanceName !== name && def && !effectiveStyles) {
          const color = DUP_PALETTE[used.size % DUP_PALETTE.length]
          effectiveStyles = { [def.figures[0].key]: { color } }
        }
      }

      let paneId: string | null
      if (paneType === 'main') {
        paneId =
          managerRef.current?.createIndicator(
            instanceName,
            true,
            CANDLE_PANE_ID,
            effectiveParams,
          ) ?? CANDLE_PANE_ID
      } else {
        paneId =
          managerRef.current?.createIndicator(name, false, undefined, effectiveParams) ?? null
      }

      const targetPaneId = paneType === 'main' ? CANDLE_PANE_ID : (paneId ?? name)
      // Apply catalog default styles (layered with any user default and/or
      // auto dup color) once mounted, so the chart matches the settings drawer.
      if (def) {
        const styles = buildIndicatorStyles(def, effectiveStyles)
        managerRef.current?.updateIndicator(targetPaneId, instanceName, { styles })
      }
      // Honor a saved default timeframe rule: if it excludes the current period,
      // the fresh indicator starts hidden even though its eye is on.
      const effVisible = effectiveIndicatorVisible(true, rule, periodRef.current, name)
      if (!effVisible) {
        managerRef.current?.updateIndicator(targetPaneId, instanceName, { visible: false })
      }

      const label = formatIndicatorLabel(name, effectiveParams)

      const next = [
        ...activeIndicatorsRef.current,
        {
          key: `ind_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name,
          instanceName,
          label,
          paneId: paneType === 'main' ? CANDLE_PANE_ID : (paneId ?? name),
          paneType,
          calcParams: effectiveParams,
          visible: true,
          styles: effectiveStyles,
          // VOL starts in its own bottom pane; the legend arrow moves it onto
          // the price pane. `placement` marks it as movable.
          placement: name === 'VOL' ? ('sub' as const) : undefined,
          timeframeVisibility: rule,
          hiddenByTimeframe: !effVisible,
        },
      ]
      // Added while the eye hides indicators: an add means "show me this".
      const added = next[next.length - 1]
      if (added.paneId === CANDLE_PANE_ID && managerRef.current?.areIndicatorsHidden()) {
        releaseIndicatorHide(next, new Set([added.key]))
        return
      }
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged, releaseIndicatorHide],
  )

  // Screenshot moved to the workspace panel's ⋯ menu (hosts call
  // `exportImage` through the imperative API); fullscreen moved to the panel
  // header — neither renders in the chart toolbar anymore.

  // Scroll-to-latest button and the context menu's "Reset view": latest bar,
  // with the price axes back on auto-fit so the current price is in view.
  const handleReset = useCallback(() => {
    managerRef.current?.goToLatest()
  }, [])

  // Escape key collapses the maximized chart (works for both controlled and
  // uncontrolled modes). Only active when the chart is currently maximized so
  // it doesn't compete with other Escape handlers in the workspace.
  useEffect(() => {
    if (!isMaximized) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onMaximize) {
          onMaximize(false)
        } else {
          setInternalMaximized(false)
        }
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isMaximized, onMaximize])

  /* ---- Indicator legend callbacks ---- */
  // Imperative chart calls are outside state updaters (pure functions in StrictMode).

  const handleToggleIndicatorVisibility = useCallback(
    (key: string) => {
      // External (host-managed) rows: the host owns the indicator's state —
      // route the action and let the updated entry flow back through props.
      const ext = externalByKeyRef.current.get(key)
      if (ext) {
        ext.onToggleVisibility?.()
        return
      }
      const prev = activeIndicatorsRef.current
      const ind = prev.find((i) => i.key === key)
      if (!ind) return
      const manager = managerRef.current
      if (!manager) return
      // The row reads OFF while the eye hides indicators, so a click means ON.
      if (manager.areIndicatorsHidden() && ind.paneId === CANDLE_PANE_ID) {
        releaseIndicatorHide(prev, new Set([key]))
        return
      }

      const nextVisible = !ind.visible
      const instanceName = ind.instanceName ?? ind.name
      // The eye toggle is the manual gate; the timeframe rule still applies on
      // top, so the effective on-chart visibility is their AND.
      const effVisible = effectiveIndicatorVisible(
        nextVisible,
        ind.timeframeVisibility,
        periodRef.current,
        ind.name,
      )
      // Live visibility toggle via overrideIndicator — keeps the pane mounted
      // and the calcParams intact. Falls back to remove+create only if the
      // indicator is somehow missing from klinecharts' registry.
      const ok = manager.updateIndicator(ind.paneId, instanceName, { visible: effVisible })
      if (!ok && effVisible) {
        if (ind.paneType === 'main' || ind.placement === 'main') {
          manager.createIndicator(instanceName, true, CANDLE_PANE_ID, ind.calcParams)
        } else {
          manager.createIndicator(instanceName, false, undefined, ind.calcParams)
        }
      }
      const updatedInd: ActiveIndicator = {
        ...ind,
        visible: nextVisible,
        hiddenByTimeframe: nextVisible && !effVisible,
      }
      const next = prev.map((i) => (i.key === key ? updatedInd : i))
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged, releaseIndicatorHide],
  )

  const handleRemoveIndicator = useCallback(
    (key: string) => {
      const ext = externalByKeyRef.current.get(key)
      if (ext) {
        ext.onRemove?.()
        return
      }
      const prev = activeIndicatorsRef.current
      const ind = prev.find((i) => i.key === key)
      if (ind) {
        managerRef.current?.removeIndicator(ind.paneId, ind.instanceName ?? ind.name)
      }
      const next = prev.filter((i) => i.key !== key)
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged],
  )

  /** Flip a placement-capable indicator (VOL) between the price-pane overlay
   *  and its own bottom pane. A flip can't be applied in place — the two modes
   *  are different registered indicators on different panes — so this removes
   *  the mounted instance and recreates the other variant. */
  const handleMoveIndicatorPane = useCallback(
    (key: string) => {
      const ext = externalByKeyRef.current.get(key)
      if (ext) {
        ext.onMovePane?.()
        return
      }
      const prev = activeIndicatorsRef.current
      const ind = prev.find((i) => i.key === key)
      if (!ind?.placement) return
      const manager = managerRef.current
      if (!manager) return

      manager.removeIndicator(ind.paneId, ind.instanceName ?? ind.name)

      const nextPlacement = ind.placement === 'main' ? 'sub' : 'main'
      let instanceName: string
      let paneId: string
      if (nextPlacement === 'main') {
        instanceName = VOL_OVERLAY_NAME
        paneId = manager.createIndicator(instanceName, true, CANDLE_PANE_ID) ?? CANDLE_PANE_ID
      } else {
        instanceName = ind.name
        paneId = manager.createIndicator(instanceName, false, undefined, ind.calcParams) ?? ind.name
        const def = getIndicatorDef(ind.name)
        if (def) {
          manager.updateIndicator(paneId, instanceName, {
            styles: buildIndicatorStyles(def, ind.styles),
          })
        }
      }
      if (ind.visible === false) {
        manager.updateIndicator(paneId, instanceName, { visible: false })
      }

      const updatedInd: ActiveIndicator = {
        ...ind,
        placement: nextPlacement,
        instanceName,
        paneId,
      }
      const next = prev.map((i) => (i.key === key ? updatedInd : i))
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged],
  )

  const handleUpdateIndicatorParams = useCallback(
    (key: string, newParams: unknown[]) => {
      // External rows have no calcParams; nothing to update.
      if (externalByKeyRef.current.has(key)) return
      const prev = activeIndicatorsRef.current
      const ind = prev.find((i) => i.key === key)
      if (!ind) return
      const manager = managerRef.current
      if (!manager) return

      const instanceName = ind.instanceName ?? ind.name
      // Live update via klinecharts overrideIndicator — no destroy/recreate.
      // Falls back to remove+create if the indicator is somehow not mounted.
      const ok = manager.updateIndicator(ind.paneId, instanceName, { calcParams: newParams })
      if (!ok) {
        manager.removeIndicator(ind.paneId, instanceName)
        if (ind.paneType === 'main' || ind.placement === 'main') {
          manager.createIndicator(instanceName, true, CANDLE_PANE_ID, newParams)
        } else {
          manager.createIndicator(instanceName, false, undefined, newParams)
        }
      }
      const updatedInd: ActiveIndicator = {
        ...ind,
        calcParams: newParams,
        label: formatIndicatorLabel(ind.name, newParams),
      }
      const next = prev.map((i) => (i.key === key ? updatedInd : i))
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged],
  )

  /** Drawer-driven live update — applies calcParams and/or styles in one shot. */
  const handleDrawerChange = useCallback(
    (
      key: string,
      patch: {
        calcParams?: unknown[]
        styles?: IndicatorStyleOverrides
        timeframeVisibility?: TimeframeVisibilityRule
        sourceTimeframe?: string
      },
    ) => {
      const prev = activeIndicatorsRef.current
      const ind = prev.find((i) => i.key === key)
      if (!ind) return
      const manager = managerRef.current
      if (!manager) return
      const def = getIndicatorDef(ind.name)

      // Source timeframe (MTF). The drawer sends the full payload, so the
      // field is authoritative each change (undefined ⇒ chart timeframe).
      const nextTf = patch.sourceTimeframe
      const tfChanged = (nextTf ?? undefined) !== (ind.sourceTimeframe ?? undefined)
      let instanceName = ind.instanceName ?? ind.name
      let paneId = ind.paneId
      if (tfChanged && isMtfEligibleIndicator(ind.name)) {
        if (instanceName !== ind.name) {
          // Already a clone alias — the wrapped calc re-reads extendData, so a
          // live override is enough (clearing the timeframe makes the wrap a
          // passthrough; no re-create needed in either direction).
          manager.updateIndicator(paneId, instanceName, {
            extendData: nextTf ? { timeframe: nextTf } : {},
          })
        } else if (nextTf) {
          // Built-in-template instance gaining a timeframe: the built-in calc
          // cannot be wrapped, so swap the instance for a clone alias.
          const used = new Set(prev.map((i) => i.instanceName ?? i.name))
          used.add(ind.name)
          const alias = allocateInstanceName(ind.name, used)
          ensureInstanceRegistered(alias, ind.name)
          manager.removeIndicator(ind.paneId, instanceName)
          if (ind.paneType === 'main') {
            manager.createIndicator(alias, true, CANDLE_PANE_ID, ind.calcParams, {
              timeframe: nextTf,
            })
            paneId = CANDLE_PANE_ID
          } else {
            paneId =
              manager.createIndicator(alias, false, undefined, ind.calcParams, {
                timeframe: nextTf,
              }) ?? alias
          }
          instanceName = alias
          if (def) {
            manager.updateIndicator(paneId, alias, {
              styles: buildIndicatorStyles(def, patch.styles),
            })
          }
        }
      }

      const updatePatch: Parameters<typeof manager.updateIndicator>[2] = {}
      if (patch.calcParams !== undefined) updatePatch.calcParams = patch.calcParams
      // Styles are authoritative too. The drawer collapses an empty override
      // set to `undefined`, which means "catalog defaults", not "no change":
      // skipping it left a reset indicator painted in its old color (a VWAP
      // reset from yellow stayed yellow, on screen and in the saved entry).
      if (def) updatePatch.styles = buildIndicatorStyles(def, patch.styles)
      // The drawer always sends the full payload, so `timeframeVisibility` is
      // authoritative each change (undefined ⇒ permissive/all timeframes). Fold
      // it into the effective on-chart visibility alongside the manual eye.
      // For an indicator with a BUILT-IN rule (VWAP), an "all on" edit is
      // stamped as an explicit empty rule — leaving it undefined would fall
      // back to the built-in and silently re-hide what the user just enabled.
      const nextRule =
        patch.timeframeVisibility ?? (BUILT_IN_TIMEFRAME_VISIBILITY[ind.name] ? {} : undefined)
      const effVisible = effectiveIndicatorVisible(ind.visible, nextRule, periodRef.current)
      updatePatch.visible = effVisible
      manager.updateIndicator(paneId, instanceName, updatePatch)
      const updatedInd: ActiveIndicator = {
        ...ind,
        instanceName,
        paneId,
        calcParams: patch.calcParams ?? ind.calcParams,
        styles: patch.styles,
        timeframeVisibility: nextRule,
        sourceTimeframe: nextTf,
        hiddenByTimeframe: ind.visible !== false && !effVisible,
        label: formatIndicatorLabel(ind.name, patch.calcParams ?? ind.calcParams, nextTf),
      }
      const next = prev.map((i) => (i.key === key ? updatedInd : i))
      setActiveIndicators(next)
      notifyIndicatorsChanged(next)
    },
    [notifyIndicatorsChanged],
  )

  // `anchor` (the gear's click point) is deliberately IGNORED: the indicator
  // legend lives at the chart's top-left, and `anchoredPanelPosition` places a
  // panel to the click's left, vertically centred on it. For a drawer taller
  // than the click's y that computes a negative top and clamps to the viewport
  // margin — pinning the settings to the top-left CORNER OF THE APP, nowhere
  // near the chart it belongs to. Centering over the chart is both correct here
  // and what a settings dialog should do.
  const handleOpenIndicatorSettings = useCallback(
    (key: string, _anchor?: { x: number; y: number }) => {
      // External rows: a custom handler wins; otherwise entries with styles
      // and/or inputs to edit get the SAME settings drawer as catalog
      // indicators (with a synthesized definition, see the drawer mount).
      const ext = externalByKeyRef.current.get(key)
      if (ext) {
        if (ext.onOpenSettings) {
          ext.onOpenSettings()
          return
        }
        if (!externalOpensNativeDrawer(ext)) return
      }
      setOverlayEdit(null)
      setEditingIndicatorKey(key)
    },
    [],
  )

  const handleLocateIndicator = useCallback((key: string) => {
    externalByKeyRef.current.get(key)?.onLocateSource?.()
  }, [])

  // Focus (pin-to-price) is host-owned and external-only: pass the row key back
  // to the external entry's handler (mirrors handleLocateIndicator). No internal
  // klinecharts indicator carries focus, so there's no native fallback path.
  const handleToggleFocusIndicator = useCallback((key: string) => {
    externalByKeyRef.current.get(key)?.onToggleFocus?.(key)
  }, [])

  /* ---- Drawing bar callbacks ---- */

  const handleSelectTool = useCallback((overlayName: string) => {
    // Effective per-tool default: the built-in default drawing color layered
    // with any prior "Save as default". Threaded into the create path so a fresh
    // drawing spawns styled — and persists that way (ChartManager carries it
    // into the create sync payload). Self-styled tools (position/measure) resolve
    // to undefined unless the user explicitly saved a default.
    const initial = resolveOverlayDefault(overlayName)
    // ChartManager owns the per-shape create routing (position tools need
    // expanding to 4 anchors); this layer adds the default style + extendData
    // (a Fib's saved per-level visibility) + highlight.
    const id = managerRef.current?.selectTool(overlayName, initial?.style, initial?.extendData)
    // Arm this tool's highlight; the manager clears it on draw-end. Choosing
    // another tool re-points the highlight (deactivating the previous one).
    setActiveTool(overlayName)
    // Returned so external drawing bars (the grid's shared bar, via
    // `EdgeChartApi.selectTool`) can track the pending overlay per cell.
    return id ?? null
  }, [])

  // Shift hotkey (from ChartManager) arms the measure/ruler tool — same path as
  // the drawing-bar button, so its selected highlight stays in sync.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    return manager.getEventBus().on('measure:requested', () => handleSelectTool('measure'))
  }, [chartInstance, handleSelectTool])

  const handleSnapToggle = useCallback(() => {
    // This used to flip a boolean and stop — the button changed colour and
    // nothing snapped. Magnet lives on the OVERLAY in klinecharts, so the mode
    // has to be pushed down to the chart. `weak` snaps only within
    // `modeSensitivity` of an OHLC value, leaving the cursor free elsewhere.
    const next = !snapMode
    setSnapMode(next)
    managerRef.current?.setOverlayMode(next ? 'weak_magnet' : 'normal')
  }, [snapMode])

  const handleLockAll = useCallback(() => {
    setLockAll((v) => {
      const chart = managerRef.current?.getChart()
      if (!chart) return v
      const newLock = !v
      chart.overrideOverlay({ lock: newLock } as Parameters<typeof chart.overrideOverlay>[0])
      return newLock
    })
  }, [])

  // Eye (drawing-bar) hide state — per category so the split menu can hide
  // drawings, indicators, or both independently. Every change is reported via
  // onHiddenStateChange so the host can persist it (state alone dies on refresh).
  const handleSetHidden = useCallback(
    (mode: HideMode, hidden: boolean) => {
      const manager = managerRef.current
      if (!manager) return
      const next = {
        drawings: mode === 'drawings' || mode === 'both' ? hidden : hiddenStateRef.current.drawings,
        indicators:
          mode === 'indicators' || mode === 'both' ? hidden : hiddenStateRef.current.indicators,
      }
      if (mode === 'drawings' || mode === 'both') manager.setOverlaysVisible(!hidden)
      if (mode === 'indicators' || mode === 'both') manager.setIndicatorsVisible(!hidden)
      setHiddenState(next)
      onHiddenStateChange?.(next)
    },
    [onHiddenStateChange],
  )

  // `s` hotkey (from ChartManager, gated on pointer-over) toggles show/hide of
  // ALL indicators + drawings — the same "both" path as the DrawingBar eye
  // button. In a grid (scanner) the host wires `onToggleVisibilityAll` to
  // broadcast the toggle across every cell; standalone charts self-toggle
  // (anything shown → hide both; both hidden → show both).
  const onToggleVisibilityAllRef = useRef(onToggleVisibilityAll)
  onToggleVisibilityAllRef.current = onToggleVisibilityAll
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    return manager.getEventBus().on('visibility:toggleAll', () => {
      if (onToggleVisibilityAllRef.current) {
        onToggleVisibilityAllRef.current()
        return
      }
      const h = hiddenStateRef.current
      handleSetHidden('both', !h.drawings || !h.indicators)
    })
  }, [chartInstance, handleSetHidden])

  const handleTrashAll = useCallback(() => {
    managerRef.current?.removeOverlay()
    setLockAll(false)
    const next = { ...hiddenStateRef.current, drawings: false }
    setHiddenState(next)
    onHiddenStateChange?.(next)
  }, [onHiddenStateChange])

  // The app's tool wheel (hold Tab) arms through the same entry point as this
  // chart's drawing bar, and runs the bar's own magnet / lock / eye / trash,
  // only where that bar exists: a chart with no drawing tools never
  // registers. Grid cells run without a bar of their own; their grid
  // registers its broadcast instead.
  useToolWheelTarget(
    containerRef,
    drawingBarAvailable
      ? {
          selectTool: handleSelectTool,
          // Tab tapped: disarm, the same as Escape mid-draw.
          cancelTool: () => {
            managerRef.current?.cancelPendingDraw()
          },
          activeTool,
          onSnapToggle: handleSnapToggle,
          snapActive: snapMode,
          onLockAll: handleLockAll,
          lockActive: lockAll,
          onSetHidden: handleSetHidden,
          hiddenState,
          onTrashAll: handleTrashAll,
        }
      : null,
  )

  // The bundle the floating DrawingBar runs on, published to the host (see
  // `onDrawingToolsChange`) so a header-hosted catalog drives the SAME
  // handlers and state. Only built when someone listens.
  const drawingToolsBundle = useMemo<DrawingToolsBundle | null>(
    () =>
      onDrawingToolsChange
        ? {
            onSelectTool: handleSelectTool,
            activeTool,
            onSnapToggle: handleSnapToggle,
            snapActive: snapMode,
            onLockAll: handleLockAll,
            lockActive: lockAll,
            onSetHidden: handleSetHidden,
            hiddenState,
            onTrashAll: handleTrashAll,
            favorites: drawingFavorites,
            onToggleFavorite: onToggleDrawingFavorite,
          }
        : null,
    [
      onDrawingToolsChange,
      handleSelectTool,
      activeTool,
      handleSnapToggle,
      snapMode,
      handleLockAll,
      lockAll,
      handleSetHidden,
      hiddenState,
      handleTrashAll,
      drawingFavorites,
      onToggleDrawingFavorite,
    ],
  )
  useEffect(() => {
    if (!onDrawingToolsChange) return
    onDrawingToolsChange(drawingToolsBundle)
    return () => onDrawingToolsChange(null)
  }, [onDrawingToolsChange, drawingToolsBundle])

  // Apply the persisted seed once the chart engine exists. Reads the SEED (not
  // live state): later changes are applied directly in handleSetHidden, and a
  // remount (ticker/day key change) re-arrives here with the persisted values.
  const applyHiddenSeed = useEffectEvent(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return
    if (initialHiddenState?.drawings) manager.setOverlaysVisible(false)
    if (initialHiddenState?.indicators) manager.setIndicatorsVisible(false)
  })
  useEffect(() => {
    applyHiddenSeed()
  }, [chartInstance])

  // Position the scroll-back pills below the OHLC tooltip row and any active
  // indicator legend rows. klinecharts draws the candle tooltip on canvas at
  // `offsetTop: 4` (themes/dark.ts), occupying ~y=4..20; the IndicatorLegend
  // wrapper starts at `top: 24` for the same reason. With 0 indicators the
  // pill sits just below the OHLC row; with N indicators it stacks below
  // those rows (height: 20 + gap: 1 each) plus 4px breathing room.
  // Count by paneId, not paneType: a VOL overlay (paneType 'sub', placement
  // 'main') also occupies a legend row on the candle pane. External (debug)
  // overlay rows count too — they stack in the same legend column.
  const mainIndicatorCount = legendIndicators.filter((i) => i.paneId === CANDLE_PANE_ID).length
  // Where the top-left info stack (sort badge, legend, pills) starts. With the
  // toolbar visible the OHLC header lives inside the toolbar strip, so the
  // stack keys off the strip's measured bottom — moving up when the OHLC fits
  // beside the chips and down when it wraps underneath. Without a toolbar the
  // OHLC renders as its own absolute row (top: 8) and the stack sits below it.
  // +2 (not +4): matches the legend's own ~8px text-to-text row rhythm when
  // the OHLC wraps to the strip's second row.
  // When the top bar (panel header) is shown, the only thing above the indicator
  // legends is the OHLC header — so hiding the OHLC should let them reclaim that
  // slot and move up. In hide-panel mode the floating chips ("tiles") occupy that
  // space instead and the legends can't move, so no reclaim there.
  // `toolbarControlsVisible` is true exactly in that hide-panel / mobile state.
  const reclaimOhlcSlot = !toolbarControlsVisible && settings.hideOverlayOHLC && !overlayLeading
  const stackBase = reclaimOhlcSlot
    ? 4 + overlayTopInset
    : toolbarVisibleProp
      ? toolbarBottom + 2
      : (settings.hideOverlayOHLC && !overlayLeading ? 4 : 22) + overlayTopInset
  // Sort-value badge (scanner mode): a left-aligned row tucked between the OHLC
  // header and the indicator legend. When present it shifts the legend + pills
  // down by its height so nothing overlaps.
  const SORT_VALUE_ROW_H = 18
  const showSortValue = !!watermarkSubtitle
  const sortValueTop = stackBase
  const sortValueOffset = showSortValue ? SORT_VALUE_ROW_H : 0
  // Main-pane legend sits below the sort-value row when shown.
  const mainLegendTop = showSortValue ? sortValueTop + SORT_VALUE_ROW_H : stackBase
  const scrollbackPillTop =
    (mainIndicatorCount > 0 ? stackBase + 21 * mainIndicatorCount + 4 : stackBase) + sortValueOffset

  // Each sub-pane (VOL / RSI / MACD …) gets its own indicator legend anchored
  // inside that pane. We track each pane's pixel top so the legend never lands
  // on top of the candle-pane OHLC header. Recomputes when indicators change,
  // the container resizes, or the user drags a pane separator.
  const [subPaneTops, setSubPaneTops] = useState<Record<string, number>>({})
  const [subPaneHeights, setSubPaneHeights] = useState<Record<string, number>>({})
  // Live y-axis strip width — keeps the bottom-right settings gear horizontally
  // centered under the price axis column as its width tier changes. Also offsets
  // the left-anchored chrome when the price axis is on the LEFT, so the OHLC
  // header / legends / watermark clear the axis instead of overlapping it.
  const [yAxisWidth, setYAxisWidth] = useState(0)
  const priceAxisOnLeft = settings.priceScalePosition === 'left'
  // "Add alert at $X" in both right-click menus; the menu's own point anchors the popover.
  const requestAlertAt = (menu: { x: number; y: number; alertPrice: { price: number } | null }) => {
    const price = menu.alertPrice?.price
    if (!onAddPriceAlert || price == null) return undefined
    return () =>
      onAddPriceAlert({
        price,
        anchorRect: { left: menu.x, top: menu.y, width: 0, height: 0 },
        axisSide: priceAxisOnLeft ? 'left' : 'right',
      })
  }
  // Indent the top-left info stack past the drawing bar ONLY when the bar can
  // actually reach it. The bar grows up from 80% of the chart height, so on a
  // tall chart its top edge sits far below the legend and the inset simply
  // shoved the indicator values off their own left margin for no reason — the
  // OHLC row (drawn by klinecharts, unaware of the inset) stayed flush left,
  // so the two disagreed. Before the first measurement, indent: that is the
  // old behaviour, and erring toward space is better than overlapping tools.
  const legendStackBottom = mainLegendTop + LEGEND_ROW_H * Math.max(1, mainIndicatorCount)
  const drawingBarReachesLegend = drawingBarTop === null || drawingBarTop < legendStackBottom
  // Keyed on the bar being ON SCREEN, not merely intended. `drawingBarVisible`
  // is the persisted intent and survives the cursor leaving the chart — the bar
  // itself tucks away (`collapsed` below uses intent AND hover), so holding the
  // indent meant the values stayed shoved right against a bar that wasn't
  // there, while the canvas-drawn OHLC row above them sat flush left.
  const drawingBarOnScreen = drawingBarAvailable && drawingBarVisible && drawingBarHovered
  const leftInset = drawingBarOnScreen && drawingBarReachesLegend ? DRAWING_BAR_OVERLAY_INSET : 0
  const overlayLeft = 4 + leftInset + (priceAxisOnLeft ? yAxisWidth : 0)
  const subPaneIds = [
    ...new Set(
      legendIndicators
        .filter((i) => i.paneType === 'sub' && i.paneId !== CANDLE_PANE_ID)
        .map((i) => i.paneId),
    ),
  ]

  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !chartInstance) return

    let rafId = 0
    const recompute = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const next = manager.getSubPaneTops()
        setSubPaneTops((prev) => {
          const keys = new Set([...Object.keys(prev), ...Object.keys(next)])
          for (const k of keys) {
            if (prev[k] !== next[k]) return next
          }
          return prev
        })
        const nextHeights = manager.getSubPaneHeights()
        setSubPaneHeights((prev) => {
          const keys = new Set([...Object.keys(prev), ...Object.keys(nextHeights)])
          for (const k of keys) {
            if (prev[k] !== nextHeights[k]) return nextHeights
          }
          return prev
        })
        // Track the y-axis strip width for the settings-gear centering. The
        // string literal matches DomPosition.YAxis — a type-only import keeps
        // the (test-mocked) runtime module untouched.
        const axisW = chartInstance.getSize?.(CANDLE_PANE_ID, 'yAxis' as DomPosition)?.width ?? 0
        setYAxisWidth((prev) => (prev === axisW ? prev : axisW))
      })
    }

    recompute()

    const ro = new ResizeObserver(recompute)
    if (chartContainerRef.current) ro.observe(chartContainerRef.current)
    chartInstance.subscribeAction(ActionType.OnPaneDrag, recompute)
    // Axis width re-tiers on data changes (price magnitude / precision).
    const bus = manager.getEventBus()
    bus.on('data:updated', recompute)
    // ...and on VIEW changes, which no other trigger here catches. Panning or
    // zooming swaps which bars are visible, so the price range (and therefore
    // the digit count in the axis labels) re-tiers without the container
    // resizing, a pane being dragged, or any new data arriving. Without this
    // the axis silently widened and everything anchored to `yAxisWidth` kept
    // the stale offset. `recompute` is already rAF-coalesced, so subscribing
    // to a per-frame action costs at most one measurement per frame.
    chartInstance.subscribeAction(ActionType.OnVisibleRangeChange, recompute)

    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
      chartInstance.unsubscribeAction(ActionType.OnPaneDrag, recompute)
      chartInstance.unsubscribeAction(ActionType.OnVisibleRangeChange, recompute)
      bus.off('data:updated', recompute)
    }
  }, [chartInstance, legendIndicators])

  return (
    <div
      ref={containerRef}
      className={className}
      data-maximized={String(isMaximized)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: settings.backgroundColor,
        position: 'relative',
        // Click-drag on the chart (crosshair, drawing, pan) must not highlight
        // the toolbar / legend / watermark text. Inline param inputs in the
        // legend re-enable selection via their own `select-text` class.
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <div
        // Named hover group (`group/chart`) — the floating toolbar's right-side
        // tool cluster fades in only while the cursor is over the chart.
        className="group/chart"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: 'relative',
          // Stop drag-pans from accidentally highlighting overlay text
          // (legends, watermark, OHLC header). The canvas isn't selectable
          // anyway; this guards the HTML overlays layered over it. Inputs
          // re-enable selection locally (see IndicatorLegend).
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        // The floating drawing tools auto-hide whenever the cursor leaves the
        // chart — collapsing back to the pencil tab (and persisting, so charts
        // open with the tools tucked away by default). The OHLC header and
        // indicator readouts also snap back to the latest bar.
        onMouseEnter={() => setDrawingBarHovered(true)}
        // `mouseenter` alone is not a reliable RECOVERY signal — it does not
        // fire when the pointer re-enters without React seeing a boundary
        // crossing. The guard makes this a no-op once hovered.
        onMouseMove={() => {
          if (!drawingBarHovered) setDrawingBarHovered(true)
        }}
        onMouseLeave={() => {
          managerRef.current?.getEventBus().emit('crosshair:leave', undefined)
          // Tuck the tools away — do NOT touch `drawingBarVisible`. That is the
          // user's intent and must survive the cursor leaving, so coming back
          // restores the bar exactly as they left it.
          setDrawingBarHovered(false)
        }}
      >
        {/* Chart canvas */}
        <div
          ref={chartContainerRef}
          data-testid="edge-chart-canvas"
          // Keyboard-layer coordination stamps, read by the host's document
          // handlers: replay claims Space while active, and letters yield to
          // an open interval buffer instead of opening the ticker search.
          data-replay-active={replayActive || undefined}
          data-interval-buffer-open={intervalSwitch != null || undefined}
          style={{ width: '100%', height: '100%', background: settings.backgroundColor }}
        />

        {/* Reload veil — in-place symbol/anchor swap (Chris,
            2026-07-30): the previous bars stay on screen while the replacement
            load is in flight (ChartManager no longer clearData()s on swap), and
            this overlay dims them until `load:settled`. Chart chrome (toolbar,
            gear, crosshair label) stays above at z-20+. pointer-events-none:
            the veil is a tint, not a click shield — panning a stale chart for
            ~200ms is harmless and blocking input would make the UI feel hung. */}
        <div
          aria-hidden="true"
          data-testid="edge-chart-reload-veil"
          className={
            'pointer-events-none absolute inset-0 z-10 transition-opacity duration-150 ' +
            (reloadVeil ? 'opacity-60' : 'opacity-0')
          }
          style={{ background: settings.backgroundColor }}
        />

        {/* Brand watermark — a small, quiet Edge "E" in the bottom-left corner,
            the platform mark. pointer-events-none so it never blocks a click on
            the chart; soft muted gray at low opacity so it recedes behind the
            data. z-10 sits above the canvas but below the interactive chrome
            (toolbar / gear at z-20+). */}
        {showWatermark && (
          <div
            aria-hidden="true"
            data-testid="edge-chart-watermark"
            className="pointer-events-none absolute z-30 select-none"
            // Lifted off the very bottom so it sits INSIDE the pane, clear of the
            // x-axis time labels. Soft muted gray via currentColor on the inlined
            // Edge mark (public/static/edge-mark.svg — the "E" path + the L-notch
            // polygon at the bottom-left).
            style={{
              bottom: 26,
              left: 8,
              // 50% comes from the color alpha (not element opacity) so the
              // black drop-shadow renders at full strength. The soft black
              // outline lifts the mark off busy candles so it stands out.
              color: 'hsl(var(--chart-text-muted) / 0.5)',
              filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.9)) drop-shadow(0 0 1px rgba(0,0,0,0.9))',
            }}
          >
            <svg width="19" height="24" viewBox="0 18.7 55.27 69.23" fill="currentColor">
              <path d="M32.38,59.52h22.9V69H19.05L18.91,18.7H54.48l-1.66,9.5H32.38V38.57H53.61v9.58H32.38Z" />
              <polygon points="55.27 78.43 55.27 87.93 0 87.93 0 60 9.5 60 9.5 78.43 55.27 78.43" />
            </svg>
          </div>
        )}

        {/* Floating glass toolbar — hovers over the canvas top edge. The OHLC
            header rides inside it as a wrapping flex item: beside the chips
            when the row has room, underneath when the panel is narrow. */}
        {toolbarVisibleProp && (
          <Toolbar
            floating
            controlsHidden={!toolbarControlsVisible}
            timeframeOnly={toolbarTimeframeOnly}
            rootRef={toolbarRootRef}
            trailing={
              !settings.hideOverlayOHLC ? (
                <OhlcHeader
                  inline
                  leading={overlayLeading}
                  chart={chartInstance}
                  eventBus={managerRef.current?.getEventBus() ?? null}
                  upColor={effectiveCandleUp}
                  downColor={effectiveCandleDown}
                  showChange={period.timespan === 'day'}
                />
              ) : (
                overlayLeading
              )
            }
            title={toolbarTitle}
            period={period}
            chartType={chartType}
            onPeriodChange={handlePeriodChange}
            favoriteTimeframes={favoriteTimeframes}
            onToggleFavoriteTimeframe={onToggleFavoriteTimeframe}
            onChartTypeChange={handleChartTypeChange}
            onAddIndicator={handleAddIndicator}
            templates={templates}
            onSaveTemplate={onSaveTemplate}
            onLoadTemplate={onLoadTemplate}
            onDeleteTemplate={onDeleteTemplate}
            onUpdateTemplate={onUpdateTemplate}
            onLayout={onLayout}
            layoutActive={layoutActive}
            onDrillDown={onDrillDown}
          />
        )}

        {/* Settings gear — bottom-right corner over the axis strip,
            surfacing on chart hover. Screenshot and
            fullscreen moved to the workspace panel chrome; the toolbar's
            right cluster is just Layout + Drill down. */}
        {toolbarVisibleProp && (
          <button
            title="Settings"
            data-action="settings"
            // Same trick as the old toolbar gear: stop the mousedown from
            // reaching SettingsModal's document-level outside-click listener.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setSettingsOpen((v) => !v)}
            // Positioning:
            //  • Scales shown — horizontally centered under the price-axis
            //    column (gear is 24px wide, so its center lands on the strip's
            //    center); falls back to a 4px corner offset until width known.
            //  • Scales hidden — no axis strip to center under, so pin to the
            //    bottom-right corner (right/bottom: 6) clear of the crosshair
            //    time label (which stops at width - 40).
            style={
              // Axis on the LEFT: center the gear under the left strip (or pin
              // bottom-left when scales are hidden). Otherwise the usual right.
              priceAxisOnLeft
                ? {
                    left: settings.hideAxisScale
                      ? 6
                      : yAxisWidth > 0 && yAxisWidth < 200
                        ? Math.max(4, Math.round(yAxisWidth / 2 - 12))
                        : 4,
                  }
                : {
                    right: settings.hideAxisScale
                      ? 6
                      : yAxisWidth > 0 && yAxisWidth < 200
                        ? Math.max(4, Math.round(yAxisWidth / 2 - 12))
                        : 4,
                  }
            }
            // outline-hidden: closing the modal with Escape flips the browser
            // into keyboard-focus mode, which would otherwise paint a focus
            // ring on the still-focused gear.
            className={
              // z-30: above the chart's axis/label layers AND above the
              // CrosshairTimeLabel overlay (z-20), so the gear is never buried
              // under the time label in either scale mode.
              'absolute z-30 flex h-6 w-6 items-center justify-center rounded outline-hidden transition-all ' +
              (settings.hideAxisScale ? 'bottom-1.5 ' : 'bottom-1 ') +
              // When scales are hidden the gear sits directly over the candles,
              // so give it a solid legible backing (canvas bg + border + shadow)
              // whenever it's revealed — keeping the hover-reveal behavior.
              (settings.hideAxisScale
                ? 'border border-[hsl(var(--chart-text)/0.15)] bg-[hsl(var(--chart-canvas)/0.9)] shadow-md '
                : '') +
              (settingsOpen
                ? 'bg-accent text-primary opacity-100'
                : 'text-[hsl(var(--chart-text-muted))] opacity-0 hover:bg-accent hover:text-[hsl(var(--chart-text))] group-hover/chart:opacity-100')
            }
          >
            <svg
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2zM12 15a3 3 0 100-6 3 3 0 000 6z" />
            </svg>
          </button>
        )}

        {/* Crosshair TIME label — replaces the native floating time label when
            the axis scales are hidden (the x-axis strip is collapsed to 1px, so
            klinecharts' own label is clipped away). Pinned to the chart-area
            bottom, centered on the crosshair. */}
        {settings.hideAxisScale && (
          <CrosshairTimeLabel
            chart={chartInstance}
            eventBus={managerRef.current?.getEventBus() ?? null}
            period={period}
            timezone={settings.timezone}
            crosshairLabelBg={settings.crosshairLabelBg}
          />
        )}

        {/* The "+" joined to the crosshair price label (CrosshairPriceAction.tsx). Only with
            the axis shown and the horizontal crosshair label on, since it attaches to it. */}
        {onAddPriceAlert &&
          !settings.hideAxisScale &&
          settings.showCrosshair !== false &&
          settings.crosshairMode !== 'vertical' && (
            <CrosshairPriceAction
              chart={chartInstance}
              manager={managerRef.current}
              eventBus={managerRef.current?.getEventBus() ?? null}
              axisOnLeft={priceAxisOnLeft}
              labelBg={effectiveCrosshairLabelBg}
              containerRef={chartContainerRef}
              onAdd={onAddPriceAlert}
            />
          )}

        {/* Earnings hover card — a thin React layer over the canvas badges.
            Shows EPS est/actual/surprise/revenue/BMO-AMC for the earnings
            event under the crosshair. Only mounts when earnings are supplied
            and the toggle is on. */}
        {settings.showEarnings && earnings && earnings.length > 0 && (
          <EarningsTooltip
            chart={chartInstance}
            eventBus={managerRef.current?.getEventBus() ?? null}
            earnings={earnings}
            timezone={settings.timezone}
          />
        )}

        {/* Split hover card - the outlined "S" badge carries no inline ratio,
            so the type / ratio / date show here on hover. Only mounts when
            splits are supplied and the toggle is on. */}
        {settings.showSplits && splits && splits.length > 0 && (
          <SplitTooltip
            chart={chartInstance}
            eventBus={managerRef.current?.getEventBus() ?? null}
            splits={splits}
            timezone={settings.timezone}
          />
        )}

        {/* Floating drawing bar — rides with the top toolbar, or stands alone
            when a host opts in via `drawingToolsEnabled` (backtest grid cells
            hide the top toolbar but still gate drawing tools behind a
            setting). Collapsed it's a pencil tab on the left edge. */}
        {drawingBarAvailable && (
          <DrawingBar
            floating
            rootRef={measureDrawingBar}
            favorites={drawingFavorites}
            onSelectTool={handleSelectTool}
            activeTool={activeTool}
            onSnapToggle={handleSnapToggle}
            onLockAll={handleLockAll}
            onSetHidden={handleSetHidden}
            onTrashAll={handleTrashAll}
            snapActive={snapMode}
            lockActive={lockAll}
            hiddenState={hiddenState}
            collapsed={!drawingBarVisible || !drawingBarHovered}
            onToggleCollapse={() => {
              setDrawingBarHovered(true)
              setDrawingBarVisible((v) => {
                onDrawingBarVisibilityChange?.(!v)
                return !v
              })
            }}
          />
        )}

        {/* Top-center watermark: "TICKER - Timeframe" (e.g. "AAPL - 1m").
              Faint, pointer-events off so it never blocks chart interaction.
              Reads the live period state so it tracks toolbar timeframe
              changes. Offset below the OHLC header row at the top of the
              candle pane. Hidden via the per-chart `hideWatermark` setting. */}
        {!settings.hideWatermark && symbol?.ticker && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              // 28 below where the old in-flow toolbar used to end (the canvas
              // now extends under the floating chips). Plus a FIXED +14 nudge so
              // the "TICKER - Timeframe" text clears the OHLC row — applied
              // uniformly on every chart and NOT tied to the active-cell state,
              // so the watermark never shifts when a cell activates.
              top: 42 + topInset,
              left: 0,
              right: 0,
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 1,
              fontFamily: CHART_FONT_FAMILY,
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: C.textSecond,
              opacity: 0.2,
            }}
          >
            {symbol.ticker} - {period.label}
          </div>
        )}

        {/* OHLC header (toolbar-less charts only — grid cells etc.). With a
              toolbar it rides inside the strip as `trailing` above. */}
        {!settings.hideOverlayOHLC && !toolbarVisibleProp && (
          <OhlcHeader
            leading={overlayLeading}
            chart={chartInstance}
            eventBus={managerRef.current?.getEventBus() ?? null}
            top={8 + overlayTopInset}
            left={overlayLeft}
            upColor={effectiveCandleUp}
            downColor={effectiveCandleDown}
            showChange={period.timespan === 'day'}
          />
        )}

        {/* Leading content alone when the OHLC row is hidden: same spot. */}
        {settings.hideOverlayOHLC && !toolbarVisibleProp && overlayLeading && (
          <div
            className="pointer-events-none absolute flex select-none items-center px-1 text-[11px] leading-none"
            style={{ top: 8 + overlayTopInset, left: overlayLeft, zIndex: 2 }}
          >
            {overlayLeading}
          </div>
        )}

        {/* Sort-value badge (scanner mode) — left-aligned, tucked between the
              OHLC header row and the indicator legend so it reads as part of the
              top-left info stack instead of a faint background watermark. */}
        {showSortValue && (
          <div
            style={{ top: sortValueTop, left: overlayLeft, zIndex: 2 }}
            // left + px-1 matches the IndicatorLegend wrapper + row padding
            // so the sort value's text lines up with the indicator labels.
            className="pointer-events-none absolute select-none whitespace-nowrap px-1 text-[11px] font-semibold text-[hsl(var(--chart-text-muted))]"
          >
            {watermarkSubtitle}
          </div>
        )}

        {/* Quick interval switch prompt (type a number → Enter) */}
        {intervalSwitch && (
          <ChangeIntervalOverlay
            buffer={intervalSwitch.buffer}
            label={intervalSwitch.label}
            valid={intervalSwitch.valid}
          />
        )}

        {/* Indicator legends (main pane + one per sub-pane). The layer owns
              the crosshair/tick-driven value readouts so their updates
              re-render only the legend rows — never this whole component.
              mainTop: below the OHLC row by default; lifted to the pane top
              when the OHLC row is hidden, pushed down a row when the
              sort-value badge occupies the slot under the OHLC header. */}
        <IndicatorLegendLayer
          chart={chartInstance}
          eventBus={managerRef.current?.getEventBus() ?? null}
          indicators={legendIndicators}
          hideLabels={!!settings.hideIndicatorLabels}
          mainTop={mainLegendTop}
          left={overlayLeft}
          mainCollapsed={mainLegendCollapsed}
          onToggleMainCollapse={toggleLegendCollapse}
          subPaneIds={subPaneIds}
          subPaneTops={subPaneTops}
          subPaneHeights={subPaneHeights}
          onToggleVisibility={handleToggleIndicatorVisibility}
          onRemove={handleRemoveIndicator}
          onUpdateParams={handleUpdateIndicatorParams}
          onOpenSettings={handleOpenIndicatorSettings}
          onMovePane={handleMoveIndicatorPane}
          onLocate={handleLocateIndicator}
          onToggleFocus={handleToggleFocusIndicator}
        />

        {/* Position-sizing readout — the React face of the POSITION_SIZE
            indicator. Compact at rest, expanding downward into a label/value
            list on hover. Sits left of the price axis strip.

            Rides the top edge (rightRailTop) rather than sitting under the
            toolbar: that strip is transparent and its only right-side
            occupant is hover-only, so reserving a band for it left ~30px of
            dead canvas at rest. */}
        {chartInstance && managerRef.current && (
          <PositionSizeReadout
            eventBus={managerRef.current.getEventBus()}
            active={legendIndicators.some((i) => i.name === POSITION_SIZE_NAME)}
            top={rightRailTop({ overlayTopInset })}
            right={rightRailRight({ yAxisWidth: priceAxisOnLeft ? 0 : yAxisWidth })}
          />
        )}

        {/* Position-plan % editor — opens where the entry's label pill was
            clicked (ChartManager relays it as plan:weightEdit). */}
        {planWeightEdit &&
          managerRef.current &&
          (() => {
            const { request } = planWeightEdit
            const equal = 100 / request.entryCount
            const effective = Array.from({ length: request.entryCount }, (_, j) =>
              Math.round(request.config.weights[j] ?? equal),
            )
            // Cascade budget: earlier display numbers are locked; this entry
            // can take at most what they left over.
            const myNum = request.displayNumbers[request.entryIndex]
            const lockedEarlier = effective.reduce(
              (a, w, j) => (request.displayNumbers[j] < myNum ? a + w : a),
              0,
            )
            // Anchor under the CLICKED pill (labels ride the plan's left edge
            // now, not the pane's left). `request.x` is widget-relative; the
            // pane's container-left is overlayLeft minus its 4px pad. Clamped
            // so the ~150px menu never overflows the container's right edge.
            const containerWidth =
              chartContainerRef.current?.clientWidth ?? Number.POSITIVE_INFINITY
            const menuLeft = Math.max(
              overlayLeft,
              Math.min(overlayLeft - 4 + (request.x ?? 12), containerWidth - 170),
            )
            return (
              <PlanWeightMenu
                top={planWeightEdit.top}
                left={menuLeft}
                current={effective[request.entryIndex]}
                maxPct={100 - lockedEarlier}
                onSelect={(pct) => {
                  managerRef.current?.overrideOverlayExtendData(request.overlayId, {
                    ...request.config,
                    weights: cascadeWeights(
                      effective,
                      request.displayNumbers,
                      request.entryIndex,
                      pct,
                    ),
                  })
                  setPlanWeightEdit(null)
                }}
                onRemove={
                  request.entryCount > 1
                    ? () => {
                        managerRef.current?.removePlanEntry(request.overlayId, request.entryIndex)
                        setPlanWeightEdit(null)
                      }
                    : undefined
                }
                onClose={() => setPlanWeightEdit(null)}
              />
            )
          })()}

        {/* Annotation text box, just above the freshly placed Annotation's
            label, centered on its anchor and kept inside the container. */}
        {annotationEdit &&
          (() => {
            const { overlayId, x, y } = annotationEdit
            const containerWidth =
              chartContainerRef.current?.clientWidth ?? Number.POSITIVE_INFINITY
            const top =
              y === undefined
                ? 60
                : Math.max(
                    overlayTopInset + 4,
                    y - ANNOTATION_STEM_RISE - ANNOTATION_INPUT_GAP - ANNOTATION_INPUT_H,
                  )
            const left = Math.max(
              overlayLeft,
              Math.min(
                (x ?? overlayLeft + ANNOTATION_INPUT_W / 2) - ANNOTATION_INPUT_W / 2,
                containerWidth - ANNOTATION_INPUT_W - 4,
              ),
            )
            return (
              <AnnotationTextInput
                key={overlayId}
                top={top}
                left={left}
                onCommit={(text) => {
                  managerRef.current?.commitAnnotationText(overlayId, text)
                  // The quick bar opened on the placement click, before the
                  // label existed; its gear must open the drawer on it.
                  setQuickBar((q) => (q && q.id === overlayId ? { ...q, extendData: text } : q))
                  setAnnotationEdit(null)
                }}
                onCancel={() => {
                  managerRef.current?.cancelAnnotationText(overlayId)
                  setAnnotationEdit(null)
                }}
              />
            )
          })()}

        {/* LoadingOlderIndicator while a scroll-back fetch is in flight. Top
              offset accounts for the IndicatorLegend stacking above. Exhausted
              history is marked on the canvas itself (historyStartMarker
              overlay, owned by ChartManager), not with a pill. */}
        {chartInstance && managerRef.current && (
          <LoadingOlderIndicator
            dataLayer={managerRef.current.getDataLayer()}
            eventBus={managerRef.current.getEventBus()}
            top={scrollbackPillTop}
            left={4 + overlayLeft}
          />
        )}

        {/* Bar-replay control strip — floating glass chips at the chart-area
            bottom-center, over the canvas but below modals. Subscribes to the
            manager's replay:* events; renders only while replay is active. */}
        {replayActive && managerRef.current && (
          <ReplayControls
            controller={managerRef.current.getReplay()}
            eventBus={managerRef.current.getEventBus()}
            period={period}
            timezone={settings.timezone}
            loopArming={replayLoopArming}
            onToggleLoop={() => managerRef.current?.toggleReplayLoop()}
          />
        )}

        {/* Scroll-to-latest overlay */}
        {scrollToLatestPos && (
          <button
            onClick={handleReset}
            style={{
              position: 'absolute',
              bottom: scrollToLatestPos.bottom,
              right: scrollToLatestPos.right,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 4,
              border: `1px solid ${C.border}`,
              background: C.bg,
              color: C.primary,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1,
              boxShadow: '0 2px 6px rgba(0,0,0,.4)',
            }}
            title="Scroll to latest bar"
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="13 17 18 12 13 7" />
              <polyline points="6 17 11 12 6 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Settings modal */}
      {settingsOpen && (
        <SettingsModal
          settings={settings}
          priceAlertsAvailable={priceAlertsAvailable}
          onChange={(s) => {
            setSettings(s)
            onSettingsChange?.(s)
          }}
          onClose={() => setSettingsOpen(false)}
          settingsProfiles={settingsProfiles}
          onSaveSettingsProfile={onSaveSettingsProfile}
          onLoadSettingsProfile={onLoadSettingsProfile}
          onDeleteSettingsProfile={onDeleteSettingsProfile}
          chartType={chartType}
          onChartTypeChange={handleChartTypeChange}
          interactionSettings={interactionSettings}
          onInteractionSettingsChange={onInteractionSettingsChange}
          // Centered over the chart, like the indicator drawer — at the wider
          // left-nav size, anchoring beside the gear click read as misplaced
          // and could hang past the viewport edge on small cells.
          centerIn={chartContainerRef}
          themeColors={settingsThemeColors}
        />
      )}

      {/* Price-axis right-click menu — shares ChartSettings with the modal, so
          toggling an item here also updates the modal (and vice versa). */}
      {priceScaleMenu && (
        <PriceScaleContextMenu
          x={priceScaleMenu.x}
          y={priceScaleMenu.y}
          addAlertPrice={priceScaleMenu.alertPrice?.text}
          onAddAlert={requestAlertAt(priceScaleMenu)}
          settings={settings}
          onChange={(s) => {
            setSettings(s)
            onSettingsChange?.(s)
          }}
          onResetAuto={() => managerRef.current?.resetPriceAxis()}
          isAuto={managerRef.current?.isPriceAxisAuto() ?? true}
          onMoreSettings={() => {
            setSettingsOpen(true)
            setPriceScaleMenu(null)
          }}
          onClose={() => setPriceScaleMenu(null)}
        />
      )}

      {/* Chart-body right-click menu — view / drawing / indicator / type /
          settings actions the chart already owns, plus day-scoped replay /
          ask-AI / drill (opt-in host callbacks, gated on a bar under cursor). */}
      {bodyMenu && (
        <ChartBodyContextMenu
          x={bodyMenu.x}
          y={bodyMenu.y}
          addAlertPrice={bodyMenu.alertPrice?.text}
          onAddAlert={requestAlertAt(bodyMenu)}
          chartType={chartType}
          indicatorsHidden={hiddenState.indicators}
          hasBarContext={bodyMenu.dayTs != null}
          onResetView={handleReset}
          onResetZoom={() => managerRef.current?.resetZoom()}
          onDrawLine={handleSelectTool}
          onRemoveDrawings={handleTrashAll}
          onToggleIndicators={() => handleSetHidden('indicators', !hiddenState.indicators)}
          autoAnalysis={(() => {
            const sr = settings.patternDetectionEnabled
            const fib = settings.autoFibEnabled
            const trend = settings.autoTrendlinesEnabled
            // The submenu is quick access for someone who has opted into the
            // Beta: once ANY auto-analysis feature is on, all three rows show
            // (checked = on) so a feature can be switched off AND back on from
            // here. With none on the submenu stays hidden — opting in is a
            // Settings decision, with the Beta caveat beside it.
            const any = sr || fib || trend
            return { sr, fib, trend, available: { sr: any, fib: any, trend: any } }
          })()}
          onToggleAutoAnalysis={(key) => {
            const field =
              key === 'sr'
                ? 'patternDetectionEnabled'
                : key === 'fib'
                  ? 'autoFibEnabled'
                  : 'autoTrendlinesEnabled'
            const s = { ...settings, [field]: !settings[field] }
            setSettings(s)
            onSettingsChange?.(s)
          }}
          autoTracesEnabled={autoTracesEnabled}
          onToggleAutoTraces={onToggleAutoTraces}
          onChartType={handleChartTypeChange}
          onSettings={() => {
            setSettingsOpen(true)
            setBodyMenu(null)
          }}
          onTagDay={
            onTagDay && bodyMenu.barTs != null ? () => onTagDay(bodyMenu.barTs!) : undefined
          }
          onReplayFromDay={
            onReplayFromDay && bodyMenu.dayTs != null
              ? () => onReplayFromDay(bodyMenu.dayTs!)
              : undefined
          }
          onAskAiAboutDay={
            onAskAiAboutDay && bodyMenu.dayTs != null
              ? () => onAskAiAboutDay(bodyMenu.dayTs!)
              : undefined
          }
          onDrillIntoDay={
            // Daily charts only: each bar there IS a day, so "this day" is unambiguous.
            onIntradayDrill && bodyMenu.dayTs != null && isDailyPeriod(period)
              ? () => onIntradayDrill(bodyMenu.dayTs!)
              : undefined
          }
          // Not day-scoped — renders regardless of bar context, unlike the
          // "…this day" rows above.
          onExcludeTicker={onExcludeTicker}
          excludeTickerLabel={symbol.ticker}
          onFindSimilarPattern={
            onFindSimilarPattern && bodyMenu.dayTs != null
              ? (scopeId: string) => onFindSimilarPattern(bodyMenu.dayTs!, scopeId)
              : undefined
          }
          findSimilarScopes={findSimilarScopes}
          onClose={() => setBodyMenu(null)}
        />
      )}

      {/* Indicator settings drawer (Inputs / Style / Defaults) */}
      {editingIndicatorKey &&
        (() => {
          // External (host-managed) rows get the same drawer with a
          // synthesized definition: Inputs only when the host declares them
          // (a script's input.*; a backtest node keeps its own), Style only
          // when it declares figures. A script that only draws lines, labels
          // or bar colors has no figures, so its drawer is the Inputs tab
          // alone. Edits stream back via onStylesChange / onCalcParamsChange,
          // each only for the part the row declares.
          const ext = externalByKeyRef.current.get(editingIndicatorKey)
          if (ext && externalOpensNativeDrawer(ext)) {
            const emitStyles = externalHasStyles(ext) ? ext.onStylesChange : undefined
            const emitParams = externalHasInputs(ext) ? ext.onCalcParamsChange : undefined
            const params = emitParams ? (ext.params ?? []) : []
            const figures = emitStyles ? (ext.figures ?? []) : []
            const emit = (patch: { styles?: IndicatorStyleOverrides; calcParams?: unknown[] }) => {
              emitStyles?.(patch.styles)
              emitParams?.(patch.calcParams)
            }
            return (
              <IndicatorSettingsDrawer
                indicatorName={ext.instanceName}
                defOverride={{
                  name: ext.instanceName,
                  label: ext.label,
                  paneType: ext.paneId === CANDLE_PANE_ID ? 'main' : 'sub',
                  params,
                  figures: figures.map((f) => ({
                    key: f.key,
                    label: f.label,
                    type: 'line',
                    defaultColor: f.defaultColor,
                  })),
                }}
                headerLabel={ext.label}
                calcParams={params.length > 0 ? ext.calcParams : undefined}
                styles={ext.styles}
                hideVisibilityTab
                onChange={emit}
                onApply={() => setEditingIndicatorKey(null)}
                onCancel={emit}
                onClose={() => setEditingIndicatorKey(null)}
                centerIn={chartContainerRef}
              />
            )
          }
          const ind = activeIndicators.find((i) => i.key === editingIndicatorKey)
          if (!ind) return null
          return (
            <IndicatorSettingsDrawer
              indicatorName={ind.name}
              headerLabel={ind.label}
              calcParams={ind.calcParams}
              styles={ind.styles}
              sourceTimeframe={ind.sourceTimeframe}
              mtfEligible={isMtfEligibleIndicator(ind.name)}
              chartPeriodMs={periodRef.current?.ms}
              mtfSourceBars={(tf) => managerRef.current?.getMtfSourceBarCount(tf)}
              // Show the EFFECTIVE rule (entry's own, else the built-in
              // catalog rule) so the Visibility tab reflects what's actually
              // hiding the indicator — and round-trips it as an explicit rule.
              timeframeVisibility={
                ind.timeframeVisibility ?? BUILT_IN_TIMEFRAME_VISIBILITY[ind.name]
              }
              onChange={(patch) => handleDrawerChange(ind.key, patch)}
              onApply={() => setEditingIndicatorKey(null)}
              onCancel={(snapshot) => handleDrawerChange(ind.key, snapshot)}
              onClose={() => setEditingIndicatorKey(null)}
              onSaveAsDefault={(payload) => {
                // A saved default applies to every future add of this
                // indicator — baking a source timeframe in would silently make
                // all new EMAs multi-timeframe. Per-instance only.
                const { sourceTimeframe: _tf, ...rest } = payload
                setIndicatorUserDefault(ind.name, rest)
              }}
              onApplyToAllCharts={
                onApplyIndicatorToAllCharts
                  ? (payload) => onApplyIndicatorToAllCharts(ind.name, payload)
                  : undefined
              }
              centerIn={chartContainerRef}
            />
          )
        })()}

      {/* Floating quick-edit bar for the selected drawing. Subset of the full
          drawer below; every control live-applies through the manager. */}
      {quickBar && (
        <OverlayQuickBar
          styles={quickBar.styles}
          overlayName={quickBar.name}
          points={quickBar.points}
          onApplyPoints={(pts) => {
            managerRef.current?.overrideOverlayPoints(quickBar.id, pts)
            setQuickBar((q) => (q ? { ...q, points: pts } : q))
          }}
          locked={quickBar.locked}
          chartRef={chartContainerRef}
          // Clear the chart's own top toolbar — same anchor the floating
          // drawing bar uses, so the two chrome layers never overlap.
          topOffset={(toolbarVisibleProp ? toolbarBottom : 0) + 10}
          onApplyStyle={(patch: OverlayStyleOverride) => {
            const next = { ...(quickBar.styles ?? {}), ...patch }
            managerRef.current?.overrideOverlayStyle(quickBar.id, next)
            setQuickBar((q) => (q ? { ...q, styles: next } : q))
          }}
          onToggleLock={() => {
            const next = !quickBar.locked
            managerRef.current?.overrideOverlay(quickBar.id, { lock: next })
            setQuickBar((q) => (q ? { ...q, locked: next } : q))
          }}
          onDuplicate={() => managerRef.current?.duplicateOverlay(quickBar.id)}
          onDelete={() => {
            managerRef.current?.removeOverlay(quickBar.id)
            setQuickBar(null)
          }}
          toolColor={selfStyledSwatch(quickBar.name)}
          onOpenSettings={() =>
            // `points` MUST be forwarded: the Coordinates tab reads the anchor
            // from here, and without it `normalizePoints(undefined)` collapses to
            // a zero anchor — the drawer showed 0 for price/time while the quick
            // bar (which gets real points) showed the true value.
            setOverlayEdit({
              id: quickBar.id,
              name: quickBar.name,
              styles: quickBar.styles,
              points: quickBar.points,
              extendData: quickBar.extendData,
            })
          }
        />
      )}

      {/* Drawing (overlay) settings drawer — Style / Coordinates / Visibility.
          Every edit live-applies through the manager (which persists + syncs). */}
      {overlayEdit && (
        <OverlaySettingsDrawer
          overlay={overlayEdit}
          centerIn={chartContainerRef}
          onApplyStyle={(style) => managerRef.current?.overrideOverlayStyle(overlayEdit.id, style)}
          onApplyPoints={(points) =>
            managerRef.current?.overrideOverlayPoints(overlayEdit.id, points)
          }
          onSetVisible={(visible) =>
            managerRef.current?.overrideOverlay(overlayEdit.id, { visible })
          }
          onApplyExtendData={(ed) => {
            managerRef.current?.overrideOverlayExtendData(overlayEdit.id, ed)
            // Keep the quick bar's copy current so its gear reopens the drawer
            // on the edited value (an annotation's label, a Fib's levels).
            setQuickBar((q) => (q && q.id === overlayEdit.id ? { ...q, extendData: ed } : q))
          }}
          onSaveDefault={(name, style, extendData) =>
            setOverlayUserDefault(
              name,
              extendData === undefined ? { style } : { style, extendData },
            )
          }
          onClose={() => setOverlayEdit(null)}
        />
      )}
    </div>
  )
})
